import * as React from "react";
import * as Label from "@radix-ui/react-label";
import { motion } from "framer-motion";
import { useTheme } from "../Theme/ThemeProvider";
import { 
  PaperPlaneIcon, 
  FileIcon,
  ImageIcon,
  Cross2Icon,
  ChevronDownIcon,
  ChevronUpIcon
} from "@radix-ui/react-icons";
import { RiLightbulbFlashLine } from 'react-icons/ri';
import { LiaLanguageSolid } from "react-icons/lia";
import { FaRegChartBar } from 'react-icons/fa';
import { TbWorldSearch } from 'react-icons/tb';
import { AudioLines } from 'lucide-react';
import { IconType } from 'react-icons';
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AudioWaveform } from '../VoiceMode/AudioWaveform';
import { VoiceCircle } from '../VoiceMode/VoiceCircle';
import { BsFillStopFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { addUploadedFile, removeUploadedFile, clearUploadedFiles, updateUploadedFile, ensureSessionId, clearTargetLanguages, setLastTranslation, setIsTranslating, setTranslationQuota, clearTranslationData, setTranslationHistory, addTranslationMessage, setTranslationError } from '../../store/chatSlice';
import type { ChatModeState } from '../../store/chatModeSlice';
import { addVoiceUserMessage, addVoiceAssistantMessage, updateLastAssistantMessage, updateLastAssistantMessageWithFunctionCall, finalizeVoiceUserMessage, finalizeVoiceAssistantMessage, cancelTranscribingMessages } from '../../store/chatModeSlice';
import { FileUploadDialog } from '../Dialogs/FileUploadDialog';
import { hideGlobalBlocking, showGlobalBlocking } from '../../store/loadingSlice';
import { UploadIcon } from '@radix-ui/react-icons';
import { useMsal } from '@azure/msal-react';
import { convertFileToPdf } from '../../utils/fileConversion.service';
import { createDocumentComparisonService } from '../../utils/documentComparison.service';
import { createDocumentTranslationService } from '../../utils/documentTranslation.service';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { MicrophonePermissionDialog } from '../Dialogs/MicrophonePermissionDialog';
import { MicrophoneSelectorDialog } from '../Dialogs/MicrophoneSelectorDialog';
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import logger from "../../utils/logger";
import { utf8ToBase64, arrayBufferToBase64 } from '../../utils/base64.utils';
import { openImage } from '../../store/chatSlice';
import { ILanguageOption } from './LanguageSelector';
import { TranslationMode } from './TranslationMode';
import { setVoiceModeActive, setVoiceModeListening, setVoiceModePaused, setVoiceModeRestarting, setShowInactivityWarning, setInactivityCountdown, setDisconnectedByInactivity, setShowNoAudioDialog } from '../../store/voiceModeSlice';
import { voiceModeService } from '../../utils/voiceMode.service';
import { checkMicrophonePermission } from '../../utils/microphonePermission';
import { InactivityWarningDialog } from '../Dialogs/InactivityWarningDialog';
import { authSelector } from "../../features/auth/authSlice";

const MAX_MB = 5;

// Wrapper to ensure a valid JSX.Element type using React.createElement
const StopIcon: React.FC<{ size?: number; color?: string }> = ({ size = 28, color = '#D10411' }) => {
  const icon = React.createElement(BsFillStopFill as any, { size, color });
  return React.createElement('span', { style: { display: 'inline-flex', lineHeight: 0 } }, icon) as React.ReactElement;
};

interface ChatOption {
  label: string;
  icon: React.ComponentType<any> | IconType;
  apiLabel: string;
}

interface ChatInputProps {
  onHeightChange?: (height: number) => void;
  onNavigateToChat?: (message: string, tools?: string[]) => void;
  onAddMessage?: (message: any) => void;
  isStreaming?: boolean;
  onStopStream?: () => void;
  chatPage?: boolean;
  initialTools?: string[];
  isImageStreaming?: boolean; // specifically track image generation streaming state
  error?: Error | null; // propagate error state to adjust generating UI
  externalValue?: string; // external value to set in the input
  onExternalValueApplied?: () => void; // callback after external value is applied
  onClearChat?: () => void; // callback to clear chat history when switching tools
  hasMessages?: boolean; // whether chat has any messages
  iconSize?: number;
  onToolChange?: (tool: string | null) => void; // callback when selected tool changes
  shouldClearTool?: boolean; // new prop to clear tool on RAG switch
}

// eslint-disable-next-line no-empty-pattern
export function ChatInput({ onHeightChange, onNavigateToChat, onAddMessage, isStreaming, onStopStream, chatPage, initialTools, isImageStreaming, error, externalValue, onExternalValueApplied, onClearChat, hasMessages, iconSize, onToolChange, shouldClearTool }: ChatInputProps) {
  // Place chatModeState selector at the very top so it's available everywhere
  const chatModeState: ChatModeState = useSelector((state: RootState) => state.chatMode);
  const [selectedTool, setSelectedTool] = React.useState<string | null>(null);
  // Clear selectedTool whenever the chat mode or RAG type changes (both directions)
  React.useEffect(() => {
    if (selectedTool !== null) {
      setSelectedTool(null);
      setIsTranslationCollapsed(false);
    }
  }, [chatModeState.mode, chatModeState.ragChatType]); // eslint-disable-line react-hooks/exhaustive-deps
  const auth = useSelector(authSelector);
  const [value, setValue] = React.useState("");
  const previousToolRef = React.useRef<string | null>(null); // Track previous tool for clearing chat on switch
  const manuallyClearedToolRef = React.useRef(false); // track manual clears to avoid auto re-select
  const [toolTextWidth, setToolTextWidth] = useState(80);
  const { accent, mode } = useTheme();
  // Track first tool used to determine which tools to show/hide
  const [firstToolUsed, setFirstToolUsed] = useState<string | null>(null);
  // Dropdown removed; tools now always visible as bubbles.
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const bottomBubbleContainerRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const uploadedFiles = useSelector((state: RootState) => state.chat.uploadedFiles);
  // (conversationStarted no longer gates thumbnails; removed selector to avoid unused lint warning)
  const sessionId = useSelector((state: RootState) => state.chat.sessionId);
  const globalBlocking = useSelector((state: RootState) => state.loading.globalBlocking);
  const { instance: msalInstance } = useMsal();
  // Voice mode loading state for home screen button
  const [voiceModeLoading, setVoiceModeLoading] = React.useState(false);
  const voiceModeState = useSelector((state: RootState) => state.voiceMode);
  const { isActive: isVoiceModeActive, isListening: isVoiceModeListening, isPaused: isVoiceModePaused, isRestarting: isVoiceModeRestarting, showInactivityWarning, inactivityCountdown, disconnectedByInactivity, showNoAudioDialog } = voiceModeState;
  const [audioLevel, setAudioLevel] = React.useState(0);
  const [frequencyData, setFrequencyData] = React.useState<Uint8Array | undefined>(undefined);
  const [showMicPermissionDialog, setShowMicPermissionDialog] = React.useState(false);
  const hasCreatedAssistantMessageRef = React.useRef(false); // Track if we've created assistant message for current turn
  const hasActiveVoiceUserMessageRef = React.useRef(false); // Track if a voice user message is currently active
  const assistantResponsePendingRef = React.useRef(false); // Track if assistant response is still streaming
  const voiceSessionStartedRef = React.useRef(false); // Track if voice session actually connected successfully
  const isVoiceModeActiveRef = React.useRef(isVoiceModeActive); // Mirror of Redux isVoiceModeActive for use inside callbacks

  // Keep isVoiceModeActiveRef in sync with Redux so callbacks never read stale state
  React.useEffect(() => {
    isVoiceModeActiveRef.current = isVoiceModeActive;
  }, [isVoiceModeActive]);

  // Store callback refs to avoid stale closures
  const audioLevelCallbackRef = React.useRef<((level: number) => void) | undefined>(undefined);
  const frequencyDataCallbackRef = React.useRef<((data: Uint8Array) => void) | undefined>(undefined);

  // Update callback refs whenever they change
  React.useEffect(() => {
    audioLevelCallbackRef.current = (level: number) => {
      if (!voiceModeCancelledRef.current) {
        setAudioLevel(level);
      }
    };
    frequencyDataCallbackRef.current = (data: Uint8Array) => {
      if (!voiceModeCancelledRef.current) {
        setFrequencyData(data);
      }
    };
  });

  // Re-register audio callbacks when component remounts during active voice mode
  React.useEffect(() => {
    if (isVoiceModeActive && voiceModeService.isConnected()) {
      logger.log('[ChatInput] Component mounted during active voice mode - updating audio callbacks');
      voiceModeService.updateAudioCallbacks({
        onAudioLevel: (level: number) => {
          if (!voiceModeCancelledRef.current) {
            setAudioLevel(level);
          }
        },
        onFrequencyData: (data: Uint8Array) => {
          if (!voiceModeCancelledRef.current) {
            setFrequencyData(data);
          }
        },
        onAssistantReady: () => {
          if (voiceModeCancelledRef.current) {
            return;
          }
          
          dispatch(setVoiceModeListening(true));
          voiceSessionStartedRef.current = true;
        }
      });
      
      // If already listening (connection already established), set listening state immediately
      if (voiceModeService.isListening()) {
        dispatch(setVoiceModeListening(true));
        voiceSessionStartedRef.current = true;
      }
    }
  }, [isVoiceModeActive, dispatch]); // Re-run when voice mode becomes active

  // Note: Callback re-registration removed as updateCallbacks method was causing issues
  // Callbacks are set during initial connection and persist throughout the session
  const voiceModeCancelledRef = React.useRef(false);

  // When voice mode is turned off externally (e.g. tab switch), reset internal voice state
  const prevVoiceModeActiveRef = React.useRef(isVoiceModeActive);
  React.useEffect(() => {
    if (prevVoiceModeActiveRef.current === true && isVoiceModeActive === false) {
      // Voice mode was just turned off - reset all internal voice state
      voiceModeCancelledRef.current = false;
      hasCreatedAssistantMessageRef.current = false;
      hasActiveVoiceUserMessageRef.current = false;
      assistantResponsePendingRef.current = false;
      setAudioLevel(0);
    }
    prevVoiceModeActiveRef.current = isVoiceModeActive;
  }, [isVoiceModeActive]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // New explicit state: true only while we have opened the native file picker and are awaiting user selection/cancel.
  // This replaces previous heuristic of showing spinner whenever tool selected & no files.
  const [awaitingFileSelection, setAwaitingFileSelection] = useState(false);
  
  // Ref to track when files are being processed (since Redux uploadedFiles updates async)
  const filesBeingProcessedRef = React.useRef(false);
  // Ref to track awaitingFileSelection for focus handler (avoids stale closure)
  const awaitingFileSelectionRef = React.useRef(false);
  // Warning for selecting more than allowed number of files (non-blocking, dismissable)
  const [selectionWarning, setSelectionWarning] = React.useState<string | null>(null);
  const [selectionWarningTitle, setSelectionWarningTitle] = React.useState<string | null>(null);
  // Separate warning state for translation mode — isolated so it never interferes with general file warnings
  const [translationWarning, setTranslationWarning] = React.useState<{ title: string; subtitle?: string; message: string } | null>(null);

  // Drag and drop state
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const [showBlockingMessage, setShowBlockingMessage] = React.useState(false);
  const [showImmediateSpinner, setShowImmediateSpinner] = React.useState(false); // Local spinner for instant feedback
  const [isFileDragInProgress, setIsFileDragInProgress] = React.useState(false); // Track if user is dragging file anywhere on window
  
  // Refs to track drag state for event handlers (avoids stale closures)
  const isFileDragInProgressRef = React.useRef(false);
  
  // Check if tools should be hidden
  // - Always hide for 3M Knowledge mode
  // - Hide for Legal, DevOps, and JIRA RAG only when actually in 3M Knowledge mode
  // - Never hide when mode is 'Ask Chat', regardless of the remembered ragChatType
  const shouldHideTools = chatModeState.mode === '3M Knowledge' && 
                          (chatModeState.ragChatType === 'Legal' || 
                           chatModeState.ragChatType === 'DevOps' ||
                           chatModeState.ragChatType === 'JIRA' ||
                           chatModeState.ragChatType !== null);
  const isDraggingOverRef = React.useRef(false);

  // Dynamic placeholder based on RAG type
  const getPlaceholder = () => {
    if (selectedTool) {
      return `Using ${getToolDisplayName(selectedTool)}...`;
    }
    
    if (isMobile) {
      return "Say something...";
    }
    
    // Customize placeholder for different RAG types — only when actually in 3M Knowledge mode
    if (chatModeState.mode === '3M Knowledge') {
      if (chatModeState.ragChatType === 'Legal') {
        return "Ask me questions about Records and Information Management (RIM), Ethics & Compliance (E&C), or Privacy Assessments...";
      } else if (chatModeState.ragChatType === 'DevOps' || chatModeState.ragChatType === 'TEBG' || chatModeState.ragChatType === 'JIRA') {
        return "Ask about " + chatModeState.ragChatType + "...";
      } else if (chatModeState.ragChatType === 'ITSM') {
        return "Ask about IT Service Management...";
      } else if (chatModeState.ragChatType === 'Everyday Insights') {
        return "What do you need to know about markets, competitors, or customers?";
      }
    }
    
    return "How can I help you today?";
  };

  // Note: File clearing when switching to 3M Knowledge is now handled by Header.tsx with confirmation dialog

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [confirmationConfig, setConfirmationConfig] = React.useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
  } | null>(null);

  // Mobile tools menu state
  const [isMobileToolsMenuOpen, setIsMobileToolsMenuOpen] = React.useState(false);
  // Voice mode error dialog state
  const [showVoiceModeError, setShowVoiceModeError] = React.useState(false);
  const [voiceModeErrorMessage, setVoiceModeErrorMessage] = React.useState('');
  
  // No audio detected dialog state is stored in Redux (survives navigation/remounts)
  
  // Microphone selector dialog state
  const [showMicSelector, setShowMicSelector] = React.useState(false);
  const [selectedMicDeviceId, setSelectedMicDeviceId] = React.useState<string | undefined>(() => {
    // Load saved microphone from localStorage
    try {
      return localStorage.getItem('selectedMicrophoneId') || undefined;
    } catch {
      return undefined;
    }
  });

  // Collapsible file preview state (only for chatPage)
  const [isFilePreviewCollapsed, setIsFilePreviewCollapsed] = React.useState(false);

  // Track if Compare mode is active (visual indicator and auto-filled prompt)
  const [isCompareActive, setIsCompareActive] = React.useState(false);

    // Track if translation has completed to prevent placeholder from being added
  const translationCompletedRef = React.useRef(false);
  const translationFileInputRef = React.useRef<HTMLInputElement>(null);
  // Collapse/expand state for translation mode
  const [isTranslationCollapsed, setIsTranslationCollapsed] = React.useState(false);
  // Track last translation request to detect changes - using Redux for persistence across navigation
  const {lastTranslation, targetLanguages, glossaryFiles, translationHistory} = useSelector((state: RootState) => state.chat);


  // Track tool switching - removed automatic clearing, will be handled with confirmation
  React.useEffect(() => {
    if (!selectedTool || !previousToolRef.current) {
      previousToolRef.current = selectedTool;
      return;
    }
    
    previousToolRef.current = selectedTool;
  }, [selectedTool]);
  // File upload dialog state
  const [isFileUploadDialogOpen, setIsFileUploadDialogOpen] = useState(false);
  // Staged files - files selected in the dialog but not yet confirmed with "Upload" button
  type StagedFile = {
    name: string;
    type: string;
    data: string;
    size: number;
    lastModified: number;
    status?: 'pending' | 'uploading' | 'uploaded' | 'error';
    key?: string;
  };
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);

  // Device detection using custom hook
  const deviceInfo = useDeviceDetection();
  const { isMobile, isTablet } = deviceInfo;

  // Auto-collapse translation mode on mobile when chat has messages
  React.useEffect(() => {
    if (selectedTool === 'translation' && translationHistory.length > 0) {
      setIsTranslationCollapsed(true);
    }
  }, [selectedTool, translationHistory.length]);

  // Handle external value changes (e.g., from gallery tile clicks)
  React.useEffect(() => {
    if (externalValue !== undefined && externalValue !== value) {
      setValue(externalValue);
      // Focus the textarea after setting value
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Move cursor to end of text
        const length = externalValue.length;
        textareaRef.current.setSelectionRange(length, length);
      }
      // Notify parent that external value was applied
      if (onExternalValueApplied) {
        onExternalValueApplied();
      }
    }
  }, [externalValue, value, onExternalValueApplied]);

  // Helper: choose upload base URL (dev vs prod) with env override
  const resolveUploadEndpoint = React.useCallback(() => {
    const apimBaseUrl = process.env.REACT_APP_APIM_BASE_URL || 'https://navigatordev.ai.3m.com';
    const envOverride = process.env.REACT_APP_FILE_UPLOAD_URL || undefined;
    const useLocal = process.env.REACT_APP_USE_LOCAL_FILE_UPLOAD === 'true';
    if (envOverride) return envOverride;
    if (useLocal) {
      return 'http://localhost:8006/ai/upload';
    }
    return `${apimBaseUrl}/nav-api/api/ai/file-upload`;
  }, []);

  // Upload a single file (meta passed directly to avoid stale selector race)
  const [uploadError, setUploadError] = useState<{ file: string; message: string } | null>(null);

  // Ensure any global blocking overlay is hidden when an upload error surfaces
  useEffect(() => {
    if (uploadError) {
      dispatch(hideGlobalBlocking());
    }
  }, [uploadError, dispatch]);

  const uploadFilesBatch = React.useCallback(async (fileMetaDirectArray: { name: string; type: string; data: string }[]) => {
    if (!fileMetaDirectArray || fileMetaDirectArray.length === 0) return;
    // Mark each as uploading (skip already uploaded)
    fileMetaDirectArray.forEach(m => {
      const existing = uploadedFiles.find(f => f.name === m.name);
      if (!existing || (existing.status !== 'uploaded' && existing.status !== 'uploading')) {
        dispatch(updateUploadedFile({ name: m.name, changes: { status: 'uploading', errorMessage: undefined }}));
      }
    });

    // Acquire token once
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'No authenticated account' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }
    const tokenRequest = { scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`], account: accounts[0] };
    let accessToken: string | undefined;
    try {
      accessToken = (await msalInstance.acquireTokenSilent(tokenRequest)).accessToken;
    } catch (err) {
      try {
        accessToken = (await msalInstance.acquireTokenPopup(tokenRequest)).accessToken;
      } catch (popupErr) {
        setUploadError({ file: fileMetaDirectArray[0].name, message: 'Token acquisition failed' });
        dispatch(hideGlobalBlocking());
        fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
        return;
      }
    }
    if (!accessToken) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'No access token' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    // Build files array with base64 normalization for csv
    const filesPayload = fileMetaDirectArray.map(meta => {
      const looksBase64 = /^[A-Za-z0-9+/=\r\n]+$/.test(meta.data.substring(0, 64));
      let contentBase64: string;
      if (meta.type === 'text/csv' && !looksBase64) {
        try {
          contentBase64 = utf8ToBase64(meta.data);
        } catch {
          contentBase64 = btoa(meta.data);
        }
      } else {
        contentBase64 = meta.data;
      }
      return { filename: meta.name, content_base64: contentBase64 };
    });

    const uploadEndpoint = resolveUploadEndpoint();
    const stableSession = sessionId || 'unknown_session';
    const generatedRequestId = `upload_${Date.now()}_${Math.random().toString(36).substring(2,10)}`;
    const generatedTimestamp = new Date().toISOString();
    const requestCore = {
      request_id: generatedRequestId,
      timestamp: generatedTimestamp,
      query_type: 'response-api',
      session: stableSession,
      prompt: 'file-upload-batch',
      metadata: { source: 'react-app' }
    };
    const requestPayload = {
      ...requestCore,
      payload: { ...requestCore },
      files: filesPayload
    };

    // Log sanitized batch payload
    try {
      const sanitized = {
        ...requestPayload,
        files: requestPayload.files.map(f => ({
          filename: f.filename,
          content_base64_head: f.content_base64.slice(0, 60) + (f.content_base64.length > 60 ? '…' : ''),
          content_base64_length: f.content_base64.length
        }))
      };
      // eslint-disable-next-line no-console
      logger.log('[upload-batch] request payload', sanitized);
      // ALSO log exact raw JSON string for verification (including full base64 + any future added fields)
      try {
        const rawString = JSON.stringify(requestPayload);
        // eslint-disable-next-line no-console
        logger.log('[upload-batch] raw-json-length', rawString.length);
        // eslint-disable-next-line no-console
        logger.log('[upload-batch] raw-json-string', rawString);
      } catch {/* ignore serialization issues */}
    } catch {}

    // Retry logic: attempt upload with at least one retry
    let resp: Response | undefined;
    const maxAttempts = 2; // 1 initial attempt + 1 retry
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        logger.log(`[upload-batch] Upload attempt ${attempt}/${maxAttempts}`);
        resp = await fetch(uploadEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestPayload)
        });
        
        // If we got a response, break out of retry loop
        if (resp) {
          logger.log(`[upload-batch] Upload succeeded on attempt ${attempt}`);
          break;
        }
      } catch (e) {
        logger.warn(`[upload-batch] Upload attempt ${attempt} failed:`, e);
        
        // If this isn't the last attempt, wait before retrying
        if (attempt < maxAttempts) {
          const retryDelay = 1000; // 1 second delay before retry
          logger.log(`[upload-batch] Waiting ${retryDelay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }
    
    // If all attempts failed, show error
    if (!resp) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'Network error during batch upload after retries' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    try {
      const rawClone = resp.clone();
      const rawText = await rawClone.text();
      const headersObject = Array.from(resp.headers.entries()).reduce((acc: Record<string,string>, [k,v]) => { acc[k]=v; return acc; },{});
      const truncated = rawText.length > 2000 ? rawText.slice(0,2000)+'…(truncated)' : rawText;
      // eslint-disable-next-line no-console
      logger.log('[upload-batch] raw response', { status: resp.status, ok: resp.ok, headers: headersObject, body_preview: truncated });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[upload-batch] failed raw log', err);
    }

    if (!resp.ok) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: `Batch upload failed (HTTP ${resp.status})` });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    let json: any = null;
    try { json = await resp.json(); } catch {}

    try {
      const uploadedFilesArray = json?.data?.content?.uploaded_files;
      const sanitizedResp = json ? {
        top_level_keys: Object.keys(json || {}),
        uploaded_files_mapping: Array.isArray(uploadedFilesArray) ? uploadedFilesArray.map((f:any)=>({ filename: f.filename, file_id: f.file_id, status: f.status })) : undefined,
        source: json?.source
      } : { raw: 'no-json' };
      // eslint-disable-next-line no-console
      logger.log('[upload-batch] response', sanitizedResp);
    } catch {}

    const multiUploaded = json?.data?.content?.uploaded_files;
    if (Array.isArray(multiUploaded) && multiUploaded.length > 0) {
      multiUploaded.forEach((f: any) => {
        if (f?.filename) {
          if (f?.file_id && f?.status === 'success') {
            dispatch(updateUploadedFile({ name: f.filename, changes: { status: 'uploaded', fileId: f.file_id } }));
          } else {
            dispatch(updateUploadedFile({ name: f.filename, changes: { status: 'error', errorMessage: f?.status || 'upload_failed' } }));
          }
        }
      });
      // Determine if any still missing fileId
      const missing = multiUploaded.filter((f:any)=>!f.file_id);
      if (missing.length > 0) {
        setUploadError({ file: missing[0].filename || fileMetaDirectArray[0].name, message: 'One or more files missing file_id.' });
      }
      // Upload complete - clear awaiting states
      logger.log('[upload-batch] Upload complete - clearing awaiting states');
      setAwaitingFileSelection(false);
      filesBeingProcessedRef.current = false;
      dispatch(hideGlobalBlocking());
    } else {
      // Fallback: attempt recursive search for any file_id
      const gatherFileIds: string[] = [];
      const visit = (obj: any, depth = 0) => {
        if (!obj || depth > 6) return;
        if (Array.isArray(obj)) { obj.forEach(el=>visit(el, depth+1)); return; }
        for (const k of Object.keys(obj)) {
          const v = obj[k];
          if (k === 'file_id' && typeof v === 'string') gatherFileIds.push(v);
          if (v && typeof v === 'object') visit(v, depth+1);
        }
      };
      visit(json);
      if (gatherFileIds.length === fileMetaDirectArray.length) {
        fileMetaDirectArray.forEach((m,i)=>{
          dispatch(updateUploadedFile({ name: m.name, changes: { status: 'uploaded', fileId: gatherFileIds[i] } }));
        });
        // Upload complete - clear awaiting states
        logger.log('[upload-batch] Upload complete (fallback path) - clearing awaiting states');
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      } else {
        setUploadError({ file: fileMetaDirectArray[0].name, message: 'Batch response did not include uploaded_files array.' });
        dispatch(hideGlobalBlocking());
      }
    }
  }, [uploadedFiles, msalInstance, dispatch, resolveUploadEndpoint, sessionId]);
  // Ensure we have a persistent session id on mount
  useEffect(() => {
    dispatch(ensureSessionId());
  }, [dispatch]);

  // Prevent browser's default drag-and-drop behavior (opening files in new tab)
  // This ensures files can only be dropped in our designated drop zone
  // Also track when user is dragging a file to disable tool selection
  useEffect(() => {
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleWindowDragEnter = (e: DragEvent) => {
      // Check if dragging files (not text or other data)
      if (e.dataTransfer?.types.includes('Files')) {
        logger.log('[window drag] File drag detected, disabling tool selection');
        setIsFileDragInProgress(true);
      }
    };
    
    const handleWindowDragLeave = (e: DragEvent) => {
      // Only clear if leaving the window entirely (relatedTarget is null)
      if (!e.relatedTarget || e.relatedTarget === null) {
        logger.log('[window drag] File drag left window, re-enabling tool selection after delay');
        // Small delay to handle the gap between drag leave and drop event
        setTimeout(() => {
          setIsFileDragInProgress(false);
        }, 100);
      }
    };
    
    const handleWindowDragEnd = (e: DragEvent) => {
      // Drag was cancelled (Esc key, or drag ended without drop)
      logger.log('[window drag] Drag operation ended/cancelled, re-enabling tool selection');
      setIsFileDragInProgress(false);
      setIsDraggingOver(false);
      setShowImmediateSpinner(false);
    };
    
    const handleContextMenu = (e: MouseEvent) => {
      // Right-click context menu cancels drag
      logger.log('[window drag] Context menu opened, cancelling drag');
      setIsFileDragInProgress(false);
      setIsDraggingOver(false);
      setShowImmediateSpinner(false);
    };
    
    const handleEscapeKey = (e: KeyboardEvent) => {
      // Cancel drag if Escape is pressed AND we're currently dragging (use refs to avoid stale closure)
      if (e.key === 'Escape' && (isFileDragInProgressRef.current || isDraggingOverRef.current)) {
        logger.log('[window drag] Escape pressed during drag, cancelling', { 
          isFileDragInProgress: isFileDragInProgressRef.current, 
          isDraggingOver: isDraggingOverRef.current
        });
        setIsFileDragInProgress(false);
        setIsDraggingOver(false);
        setShowImmediateSpinner(false);
        isFileDragInProgressRef.current = false;
        isDraggingOverRef.current = false;
      }
    };
    
    const handleMouseDown = (e: MouseEvent) => {
      // Right-click cancels drag
      if (e.button === 2) {
        logger.log('[window drag] Right-click detected, cancelling drag');
        setIsFileDragInProgress(false);
        setIsDraggingOver(false);
        setShowImmediateSpinner(false);
      }
    };

    // Prevent default behavior for drag events on the entire window (bubble phase, not capture)
    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('dragend', handleWindowDragEnd);
    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('drop', preventDefaults);
    window.addEventListener('keydown', handleEscapeKey);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('contextmenu', handleContextMenu); // Right-click menu also cancels

    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('dragend', handleWindowDragEnd);
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('drop', preventDefaults);
      window.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Update tool text width based on window size with debouncing (visual only)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const updateWidth = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setToolTextWidth(40);
      } else if (width < 640) {
        setToolTextWidth(60);
      } else if (width < 768) {
        setToolTextWidth(80);
      } else if (width < 1024) {
        setToolTextWidth(100);
      } else {
        setToolTextWidth(120);
      }
    };

    const debouncedUpdateWidth = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateWidth, 150);
    };

    updateWidth();
    window.addEventListener('resize', debouncedUpdateWidth);
    return () => {
      window.removeEventListener('resize', debouncedUpdateWidth);
      clearTimeout(timeoutId);
    };
  }, []);

  // Track previous height to avoid unnecessary updates
  const previousHeightRef = React.useRef<number>(0);
  const hasReportedHeightRef = React.useRef<boolean>(false); // Track if we've reported a real height

  // Auto-resize textarea
  React.useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Step 1: Reset to base state to get clean content measurement
    // Larger base height for tablets (64px vs 56px)
    const baseHeight = isTablet ? 64 : 56;
    textarea.style.height = baseHeight + 'px';
    textarea.style.paddingBottom = '16px';
    textarea.style.overflowY = 'hidden';
    
    // Step 2: Measure the actual content height
    const scrollHeight = textarea.scrollHeight;
    const contentHeight = Math.max(scrollHeight, baseHeight); // Never less than baseHeight
    const maxContentHeight = 128;
    
    // Step 3: Determine if we need scrolling based on content only
    const needsScrolling = contentHeight > maxContentHeight;
    const finalHeight = needsScrolling ? maxContentHeight : contentHeight;
    
    // Step 4: Apply settings (no extra space needed - bubble is outside textarea)
    textarea.style.height = finalHeight + 'px';
    textarea.style.overflowY = needsScrolling ? 'auto' : 'hidden';
  }, [value, isTablet]);

  // Calculate and report total component height - runs AFTER render
  React.useEffect(() => {
    if (!bottomBubbleContainerRef.current || !onHeightChange) {
      return; // Skip measurement while dropdown is open
    }
    
    // Wait for browser to paint, then measure
    const rafId = requestAnimationFrame(() => {
      if (!bottomBubbleContainerRef.current) return;
      
      const baseHeight = isTablet ? 64 : 56;
      const maxContentHeight = 128;
      
      // Calculate offsets
      const BASE_PAGE_OFFSET = !chatPage ? 110 : 0;
      const GREETING_INPUT_PADDING = !chatPage ? (iconSize || 32) : 0;
      const GREETING_SPACER = !chatPage ? 36 : 0;
      const EXTRA_GALLERY_OFFSET = BASE_PAGE_OFFSET + GREETING_INPUT_PADDING + GREETING_SPACER;
      
      // Measure the actual height of the bottom bubble container (tools + files + dropdown)
      const bottomBubbleHeight = bottomBubbleContainerRef.current.offsetHeight;
      
      // Calculate finalHeight from textarea if available
      let currentFinalHeight = baseHeight;
      if (textareaRef.current) {
        const currentScrollHeight = textareaRef.current.scrollHeight;
        const currentContentHeight = Math.max(currentScrollHeight, baseHeight);
        const currentNeedsScrolling = currentContentHeight > maxContentHeight;
        currentFinalHeight = currentNeedsScrolling ? maxContentHeight : currentContentHeight;
      }
      
      const totalComponentHeight = currentFinalHeight + bottomBubbleHeight + EXTRA_GALLERY_OFFSET;
      
      // Only update if height changed by more than 10px to prevent measurement fluctuations and feedback loops
      if (Math.abs(totalComponentHeight - previousHeightRef.current) > 10) {
        previousHeightRef.current = totalComponentHeight;
        hasReportedHeightRef.current = true; // Mark that we've reported a real height
        onHeightChange(totalComponentHeight);
      }
    });
    
    return () => cancelAnimationFrame(rafId);
  }, [value, selectedTool, uploadedFiles.length, onHeightChange, chatPage, iconSize, isTablet, isFilePreviewCollapsed, translationHistory.length, isTranslationCollapsed]);

  // Report initial height on mount to ensure padding is correct immediately
  React.useEffect(() => {
    if (!onHeightChange) return;
    
    // Only report initial height if we haven't reported a real height yet
    if (hasReportedHeightRef.current) {
      return;
    }
    
    // Report default height immediately when component mounts
    const baseHeight = isTablet ? 64 : 56;
    const BASE_PAGE_OFFSET = !chatPage ? 110 : 0;
    const GREETING_INPUT_PADDING = !chatPage ? (iconSize || 32) : 0;
    const GREETING_SPACER = !chatPage ? 36 : 0;
    const EXTRA_GALLERY_OFFSET = BASE_PAGE_OFFSET + GREETING_INPUT_PADDING + GREETING_SPACER;
    
    // Initial height = base textarea + minimal tool bar (approximately 60px)
    const initialHeight = baseHeight + 60 + EXTRA_GALLERY_OFFSET;
    previousHeightRef.current = initialHeight;
    onHeightChange(initialHeight);
  }, [onHeightChange, chatPage, iconSize, isTablet]);


  // Initialize selected tool from initialTools prop, but only if not in RAG mode
  useEffect(() => {
    const isRagMode = chatModeState.mode !== 'Ask Chat';
    if (!isRagMode && initialTools && initialTools.length > 0 && !selectedTool && !manuallyClearedToolRef.current) {
      setSelectedTool(initialTools[0]);
    }
  }, [initialTools, selectedTool, chatModeState.mode, chatModeState.ragChatType]);

  // Set firstToolUsed when initialTools is provided and conversation starts (chatPage mode)
  useEffect(() => {
    if (chatPage && initialTools && initialTools.length > 0 && !firstToolUsed) {
      setFirstToolUsed(initialTools[0]);
    }
  }, [chatPage, initialTools, firstToolUsed]);


  // Notify parent when selected tool changes
  useEffect(() => {
    onToolChange?.(selectedTool);
  }, [selectedTool, onToolChange]);


  // Clear selected tool when switching RAG chat types
  useEffect(() => {
    if (chatModeState.ragChatType) {
      // When switching to a RAG type, clear any selected tool so the placeholder updates
      setSelectedTool(null);
    }
  }, [chatModeState.ragChatType]);

  // Dropdown toggle removed.

  const chatOptions: ChatOption[] = React.useMemo(() => [
    // Removed standalone File Upload tool – uploads now handled either in general chat (no selected tool) or data_analysis.
    { label: 'Web Search', icon: TbWorldSearch, apiLabel: 'web_search' },
    { label: 'Reasoning Model', icon: RiLightbulbFlashLine, apiLabel: 'reasoning' },
    { label: 'Create Image', icon: ImageIcon, apiLabel: 'image_generation' },
    { label: 'Data Analysis', icon: FaRegChartBar, apiLabel: 'data_analysis' },
    { label: 'Document Translation', icon: LiaLanguageSolid, apiLabel: 'translation' },
  ], []);

  const handleOptionClick = (apiLabel: string, event?: React.MouseEvent | React.MouseEvent<HTMLButtonElement> & { nativeEvent?: any }) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Check if we have uploaded files and are switching to a different tool (or selecting a tool when none selected)
    if (uploadedFiles.length > 0 && selectedTool !== apiLabel) {
      const hasImages = uploadedFiles.some(f => 
        /image\/(png|jpeg|jpg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name)
      );
      const hasDataFiles = uploadedFiles.some(f => 
        /text\/csv/.test(f.type) || /application\/vnd\.(ms-excel|openxmlformats-officedocument\.spreadsheetml\.sheet)/.test(f.type) ||
        /\.(csv|xls|xlsx)$/i.test(f.name)
      );
      const hasTextFiles = uploadedFiles.some(f => 
        /application\/(pdf|msword|vnd\.openxmlformats-officedocument\.wordprocessingml\.document|vnd\.(ms-powerpoint|openxmlformats-officedocument\.presentationml\.presentation))/.test(f.type) ||
        /\.(pdf|doc|docx|ppt|pptx|txt)$/i.test(f.name)
      );
      
      let shouldWarn = false;
      let warningMessage = '';
      
      // Check for incompatible tool selections based on file types
      if (apiLabel === 'reasoning' && (hasImages || hasDataFiles || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'The Reasoning tool does not support uploaded files. Switching to this tool will clear your chat history and remove all uploaded files. Are you sure you want to continue?';
      } else if (apiLabel === 'image_generation' && (hasDataFiles || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'The Image Generation tool only supports image files. Switching to this tool will clear your chat history and remove all incompatible files. Are you sure you want to continue?';
      } else if (apiLabel === 'web_search' && hasDataFiles) {
        shouldWarn = true;
        warningMessage = 'The Web Search tool does not support data analysis files (CSV/Excel). Switching to this tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      } else if (apiLabel === 'data_analysis' && (hasImages || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'The Data Analysis tool only supports CSV and Excel files. Switching to this tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      } else if (uploadedFiles.length > 0 && selectedTool !== apiLabel) {
        // Even if files are compatible, warn when switching tools with files uploaded
        shouldWarn = true;
        warningMessage = 'Switching tools will clear your chat history and all uploaded files. Are you sure you want to continue?';
      } else if (selectedTool === 'translation' && apiLabel !== 'translation' && (hasTextFiles || hasDataFiles)) {
        shouldWarn = true;
        warningMessage = 'The Document Translation tool only supports document files (PDF, Word, PowerPoint, Excel). Switching to another tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      }
      
      // If we should warn, show confirmation dialog
      if (shouldWarn) {
        setConfirmationConfig({
          title: 'Switch Tool?',
          message: warningMessage,
          onConfirm: () => {
            // Clear history and files
            if (onClearChat) {
              onClearChat();
            }
            dispatch(clearUploadedFiles());
            setShowConfirmation(false);
            setConfirmationConfig(null);
            // Now set the new tool
            proceedWithToolSelection(apiLabel);
            dispatch(clearTranslationData());
            setIsTranslationCollapsed(false);
          }
        });
        setShowConfirmation(true);
        // Don't proceed - keep existing tool if user cancels
        return;
      }
    }
    
    // Check if switching from one tool to another (when there are messages but no files)
    if (selectedTool && selectedTool !== apiLabel && hasMessages && uploadedFiles.length === 0) {
      // Show confirmation dialog for tool switching only if there are messages
      setConfirmationConfig({
        title: 'Switch Tool?',
        message: 'Switching tools will clear your chat history. Are you sure you want to continue?',
        onConfirm: () => {
          // Clear history
          if (onClearChat) {
            onClearChat();
          }
          setShowConfirmation(false);
          setConfirmationConfig(null);
          // Now set the new tool
          proceedWithToolSelection(apiLabel);
          dispatch(clearTranslationData());
          setIsTranslationCollapsed(false);
        }
      });
      setShowConfirmation(true);
      return;
    }
    
    // No confirmation needed, proceed directly
    proceedWithToolSelection(apiLabel);
  };

  const proceedWithToolSelection = (apiLabel: string) => {
    // Close mobile menu if open
    if (isMobile) {
      setIsMobileToolsMenuOpen(false);
    }
    setSelectedTool(apiLabel);

    if (apiLabel === 'data_analysis') {
      // When activating data analysis, evaluate existing files.
      const isValidAnalysisType = (f: any) => {
        const t = (f.type || '').toLowerCase();
        const n = (f.name || '').toLowerCase();
        return t === 'text/csv' || t === 'application/vnd.ms-excel' || t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || /\.csv$/i.test(n) || /\.(xls|xlsx)$/i.test(n);
      };
      const existing = [...uploadedFiles];
      const valid = existing.filter(isValidAnalysisType);
      const invalid = existing.filter(f => !isValidAnalysisType(f));
      // Remove invalids with warning alert if any
      if (invalid.length > 0) {
        invalid.forEach(f => dispatch(removeUploadedFile(f.name)));
        // Provide filenames for transparency (truncate if many)
        const names = invalid.map(f=>f.name).slice(0,5);
        const more = invalid.length > names.length ? ` +${invalid.length - names.length} more` : '';
        setSelectionWarning(`Removed ${invalid.length} invalid file(s): ${names.join(', ')}${more}. Only CSV or Excel (max 2, <16MB each) are allowed for Data Analysis.`);
      }
      // If we have valid files already, upload any that lack a fileId
      if (valid.length > 0) {
        const toUpload = valid.filter(f => !f.fileId).map(f => ({ name: f.name, type: f.type, data: f.data }));
        if (toUpload.length > 0) {
          uploadFilesBatch(toUpload).finally(() => {
            dispatch(hideGlobalBlocking());
          });
        }
        // Do NOT trigger file picker since at least one valid file exists.
      } else if (invalid.length === 0) {
        // No valid files and no invalid removal warning -> open FileUploadDialog immediately
        setIsFileUploadDialogOpen(true);
      } else {
        // Invalid removal warning is showing; defer opening dialog until user clicks OK
        // We rely on selectionWarning OK handler to detect zero valid files and open dialog then.
      }
    }
    
    if (apiLabel === 'translation') {
       const willNavigate = !chatPage && onNavigateToChat;
      if (willNavigate) {
        onNavigateToChat('', ['translation']);
      }
    }
    
    // Focus textarea after tool selection - but not on mobile/tablet to avoid triggering keyboard
    if (!isMobile && !isTablet) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  };

  const handleRemoveTool = () => {
    // Show confirmation dialog for tool removal only if there are messages
    if (hasMessages) {
      setConfirmationConfig({
        title: 'Remove Tool?',
        message: 'Removing this tool will clear your chat history and all uploaded files. Are you sure you want to continue?',
        onConfirm: () => {
          // Clear history and reset to home state when tool is removed
          if (onClearChat) {
            onClearChat();
          }
          
          manuallyClearedToolRef.current = true;
          setSelectedTool(null);
          setAwaitingFileSelection(false);
          dispatch(clearUploadedFiles());
          dispatch(clearTranslationData());
          setIsTranslationCollapsed(false);
          dispatch(hideGlobalBlocking());
          setShowConfirmation(false);
          setConfirmationConfig(null);
          if (onClearChat) {
            onClearChat();
          }
        }
      });
      setShowConfirmation(true);
    } else {
      // No messages, just remove without confirmation
      if (onClearChat) {
        onClearChat();
      }
      manuallyClearedToolRef.current = true;
      setSelectedTool(null);
      setAwaitingFileSelection(false);
      dispatch(clearUploadedFiles());
      dispatch(clearTranslationData());
      setIsTranslationCollapsed(false);
      dispatch(hideGlobalBlocking());
    }
  };
  
  const getToolDisplayName = (apiLabel: string) => {
    const option = chatOptions.find(opt => opt.apiLabel === apiLabel);
    return option?.label || apiLabel;
  };

  // Listen for gallery tool selection events
  useEffect(() => {
    const handleGalleryToolSelection = (event: Event) => {
      const customEvent = event as CustomEvent<{ tool: string }>;
      const toolName = customEvent.detail.tool;
      
      logger.log('[ChatInput] Gallery tool selection event received:', toolName);
      
      // Map gallery tool names to API labels
      const toolMapping: { [key: string]: string } = {
        'image_generation': 'image_generation',
        'web_search': 'web_search',
        'reasoning': 'reasoning',
        'data_analysis': 'data_analysis',
        'upload_file': 'upload_file', // For general file upload
        'none': 'none', // Explicitly disable any selected tool
        'translation': 'translation'
      };
      
      const apiLabel = toolMapping[toolName];
      
      logger.log('[ChatInput] Mapped to apiLabel:', apiLabel);
      
      if (apiLabel === 'upload_file') {
        // Trigger file upload dialog for general chat (BYOD) - same as clicking BYOD button
        logger.log('[ChatInput] Triggering file upload dialog');
        triggerAdditionalUpload();
      } else if (apiLabel === 'none') {
        // Disable any currently selected tool (switch to general chat mode)
        logger.log('[ChatInput] Clearing tool selection');
        manuallyClearedToolRef.current = true;
        setSelectedTool(null);
        dispatch(clearTargetLanguages());
      } else if (apiLabel && apiLabel !== '') {
        // Directly call proceedWithToolSelection to bypass confirmation dialogs
        // Gallery tiles should act as direct tool selection
        logger.log('[ChatInput] Proceeding with tool selection:', apiLabel);
        proceedWithToolSelection(apiLabel);
      }
    };
    
    window.addEventListener('galleryToolSelection', handleGalleryToolSelection);
    return () => window.removeEventListener('galleryToolSelection', handleGalleryToolSelection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedValue = value.trim();
    
    // If Compare mode is active, trigger comparison instead of regular chat
    if (isCompareActive && uploadedFiles.length === 2) {
      await handleCompareDocuments();
      setIsCompareActive(false); // Reset after comparison
      return;
    }
    
    // DATA ANALYSIS GATING: When the data_analysis tool is active we require at least one uploaded file
    // before allowing a message to be sent. This guarantees the backend receives contextual data to analyze.
  if (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0) return;
    if (!trimmedValue || !onNavigateToChat || isStreaming) return;
    
    // Track first tool used when conversation starts
    if (!firstToolUsed && selectedTool) {
      setFirstToolUsed(selectedTool);
    }
    
    // Send with selected tool (if any)
    const tools = selectedTool ? [selectedTool] : undefined;
    onNavigateToChat(trimmedValue, tools);
    setValue('');
    // Don't clear selectedTool - keep it selected for next message
  };

  const startVoiceMode = async () => {
    // Set voice mode state IMMEDIATELY for instant UI transition
    dispatch(setVoiceModeActive(true));
    setVoiceModeLoading(true);
    
    // Clear any previous inactivity flags
    dispatch(setShowInactivityWarning(false));
    dispatch(setInactivityCountdown(0));
    dispatch(setDisconnectedByInactivity(false));
    
    // Reset cancellation flag
    voiceModeCancelledRef.current = false;
    hasCreatedAssistantMessageRef.current = false;
    hasActiveVoiceUserMessageRef.current = false;
    assistantResponsePendingRef.current = false;
    
    // Check microphone permission (async, but UI already transitioned)
    const permissionState = await checkMicrophonePermission();
    if (permissionState === 'denied') {
      // Revert state if permission denied
      dispatch(setVoiceModeActive(false));
      setVoiceModeLoading(false);
      setShowMicPermissionDialog(true);
      return;
    }
    
    // Set MSAL instance for authentication
    voiceModeService.setMsalInstance(msalInstance);
    
    // Determine voice mode server URL
    // Use environment variable if set, otherwise fall back to production endpoint
    const voiceServerUrl = process.env.REACT_APP_VOICE_MODE_URL || 'wss://ai-np-apim-main.mmm.com';
    
    // Connect to Socket.IO server
    try {
      await voiceModeService.connect(voiceServerUrl, {
        deviceId: selectedMicDeviceId,
        onStatusChange: (status) => {
          // Check if cancelled before processing
          if (voiceModeCancelledRef.current) {
            return;
          }
          
          logger.log('[ChatInput] Voice mode status:', status);
          if (status === 'error' || status === 'disconnected') {
            // Voice mode has stopped - update UI to reflect this
            logger.error('[ChatInput] Voice mode disconnected/error - updating UI state to inactive');
            setVoiceModeLoading(false);
            dispatch(setVoiceModeActive(false));
            dispatch(setVoiceModeListening(false));
            hasCreatedAssistantMessageRef.current = false;
            hasActiveVoiceUserMessageRef.current = false;
            assistantResponsePendingRef.current = false;
          }
        },
        onConnected: () => {
          // Socket connected - show voice mode UI immediately for faster perceived startup
          if (voiceModeCancelledRef.current) {
            return;
          }
                    
          // Navigate to chat page immediately on connection
          // This happens BEFORE assistant sends greeting, preventing message splitting
          if (onNavigateToChat && !chatPage) {
            onNavigateToChat('', []); // Navigate with empty message
          }
          
          // Show voice mode UI immediately - user sees the transition right away
          // Audio won't work until onAssistantReady, but UI feels much faster
          setVoiceModeLoading(false);
          dispatch(setVoiceModeActive(true));
        },
        onAssistantReady: () => {
          // Check if cancelled before proceeding
          if (voiceModeCancelledRef.current) {
            return;
          }
          
          // Enable listening state now that audio capture is running
          // This activates the waveform visualization
          logger.log('[ChatInput] Enabling audio waveform visualization');
          dispatch(setVoiceModeListening(true));
          
          // Mark that the session successfully started
          voiceSessionStartedRef.current = true;
          logger.log('[ChatInput] Voice session started successfully');
          
          // Don't create initial user message here - wait for backend speech_started event
          // DON'T reset hasCreatedAssistantMessageRef - if assistant already started responding,
          // we want to continue the same message, not create a new one when audio worklet loads
          assistantResponsePendingRef.current = false;
          hasActiveVoiceUserMessageRef.current = false;
        },
        onAudioLevel: (level) => {
          if (voiceModeCancelledRef.current) return;
          setAudioLevel(level);
        },
        onFrequencyData: (data: Uint8Array) => {
          // Update state to trigger re-renders for waveform visualization
          if (!voiceModeCancelledRef.current) {
            setFrequencyData(data);
          }
        },
        onAudioPlayback: (audioHex: string) => {
          // Received audio chunk from assistant
          if (voiceModeCancelledRef.current) return;
          
          logger.log('[ChatInput] Received audio playback chunk from assistant');
          assistantResponsePendingRef.current = true;
          
          // Audio playback happens automatically in voiceModeService
          // We don't need to create a message here - text_delta will handle that
        },
        onAssistantResponseStart: () => {
          if (voiceModeCancelledRef.current) return;

          assistantResponsePendingRef.current = true;
          hasCreatedAssistantMessageRef.current = false;
        },
        onTextDelta: (text: string) => {
          // Received text transcript chunk from assistant
          if (voiceModeCancelledRef.current) return;
          
          logger.log('[ChatInput] Received text delta:', text);
          
          // If this is the first text delta, create the assistant message
          if (!hasCreatedAssistantMessageRef.current) {
            hasCreatedAssistantMessageRef.current = true;
            dispatch(addVoiceAssistantMessage());
          }
          assistantResponsePendingRef.current = true;
          
          // Update the last assistant message with new text
          dispatch(updateLastAssistantMessage({ textDelta: text }));
        },
        onUserSpeechStart: () => {
          if (voiceModeCancelledRef.current) return;

          // Backend confirmed the user spoke - now it's safe to clear the inactivity countdown
          dispatch(setShowInactivityWarning(false));
          dispatch(setInactivityCountdown(0));

          if (!hasActiveVoiceUserMessageRef.current) {
            hasActiveVoiceUserMessageRef.current = true;
            dispatch(addVoiceUserMessage());
          }

          assistantResponsePendingRef.current = false;
          hasCreatedAssistantMessageRef.current = false;
        },
        onUserTranscriptStart: () => {
          if (voiceModeCancelledRef.current) return;

          logger.log('[ChatInput] onUserTranscriptStart called');
          // Always create a new message for each transcript start
          hasActiveVoiceUserMessageRef.current = true;
          dispatch(addVoiceUserMessage());
        },
        onUserTranscriptDelta: (_text: string) => {
          if (voiceModeCancelledRef.current) return;

          if (!hasActiveVoiceUserMessageRef.current) {
            hasActiveVoiceUserMessageRef.current = true;
            dispatch(addVoiceUserMessage());
          }
        },
        onFunctionCall: (message: string) => {
          // Function call status or sources
          if (voiceModeCancelledRef.current) return;
          
          logger.log('[ChatInput] Function call:', message);
          
          // Ensure we have an assistant message to append to
          if (!hasCreatedAssistantMessageRef.current) {
            logger.log('[ChatInput] Creating assistant message for function call');
            hasCreatedAssistantMessageRef.current = true;
            dispatch(addVoiceAssistantMessage());
          }
          
          // Try to parse as JSON (for sources data)
          try {
            const parsed = JSON.parse(message);
            if (parsed.sources && Array.isArray(parsed.sources)) {
              // Sources data received
              logger.log('[ChatInput] Received sources:', parsed.sources);
              dispatch(updateLastAssistantMessageWithFunctionCall({ 
                sources: parsed.sources
              }));
              return;
            }
          } catch {
            // Not JSON, treat as regular function call message
          }
          
          // Parse function type from message
          let functionType: 'web_search' | 'unknown' = 'unknown';
          if (message.toLowerCase().includes('search') && message.toLowerCase().includes('web')) {
            functionType = 'web_search';
          }
          
          // Add function call metadata to assistant message
          dispatch(updateLastAssistantMessageWithFunctionCall({ 
            functionCall: {
              type: functionType,
              message,
              isActive: true
            }
          }));
        },
        onSearchSources: (data: { query: string; sources: Array<{ url: string; domain: string; title: string }> }) => {
          // Search sources from AWS Bedrock Nova grounding
          if (voiceModeCancelledRef.current) return;
          
          logger.log('[ChatInput] Received search sources:', data);
          
          // Ensure we have an assistant message to append to
          if (!hasCreatedAssistantMessageRef.current) {
            logger.log('[ChatInput] Creating assistant message for search sources');
            hasCreatedAssistantMessageRef.current = true;
            dispatch(addVoiceAssistantMessage());
          }
          
          // Convert backend format (url, domain, title) to frontend format (url, title, snippet)
          const formattedSources = data.sources.map(source => ({
            url: source.url,
            title: source.title || source.domain, // Use title if available, fallback to domain
            snippet: source.domain // Use domain as snippet for now
          }));
          
          // Add sources to assistant message
          dispatch(updateLastAssistantMessageWithFunctionCall({ 
            sources: formattedSources
          }));
        },
        onSearchProgress: (data: { message: string; query: string; elapsed: number }) => {
          // Search progress updates
          if (voiceModeCancelledRef.current) return;
          
          logger.log('[ChatInput] Search progress:', data);
          
          // Ensure we have an assistant message to append to
          if (!hasCreatedAssistantMessageRef.current) {
            logger.log('[ChatInput] Creating assistant message for search progress');
            hasCreatedAssistantMessageRef.current = true;
            dispatch(addVoiceAssistantMessage());
          }
          
          // Update function call status with progress message
          dispatch(updateLastAssistantMessageWithFunctionCall({ 
            functionCall: {
              type: 'web_search',
              message: data.message,
              isActive: true
            }
          }));
        },
        onTranscriptionComplete: (transcript: string, audioData?: string[], messageId?: string) => {
          // User's speech has been fully transcribed
          if (voiceModeCancelledRef.current) return;
          
          logger.log('[ChatInput] User transcription complete:', transcript);
          // Don't pass ID - Redux will find the first transcribing message
          dispatch(finalizeVoiceUserMessage({ transcript, audioData }));
          hasActiveVoiceUserMessageRef.current = false;
        },
        onAssistantResponseComplete: (audioData?: string[]) => {
          if (voiceModeCancelledRef.current) return;

          if (hasActiveVoiceUserMessageRef.current) {
            dispatch(finalizeVoiceUserMessage({ transcript: '' }));
            hasActiveVoiceUserMessageRef.current = false;
          }

          // Finalize assistant message with audio data
          if (hasCreatedAssistantMessageRef.current) {
            dispatch(finalizeVoiceAssistantMessage({ audioData }));
          }

          hasCreatedAssistantMessageRef.current = false;
          assistantResponsePendingRef.current = false;
        },
        onError: (error) => {
          // Always handle errors even if cancelled
          console.error('[ChatInput] Voice mode error:', error);
          
          // Only add error message to chat history if the session actually started
          // If session never started (connection errors), don't pollute chat history
          if (voiceSessionStartedRef.current) {
            // Add error message to chat history so user can see it in context
            // Create assistant message if one doesn't exist
            if (!hasCreatedAssistantMessageRef.current) {
              hasCreatedAssistantMessageRef.current = true;
              dispatch(addVoiceAssistantMessage());
            }
            
            // Append error message to the assistant's message
            const errorText = `\n\n⚠️ **Error**: ${error.message || 'An error occurred during voice mode.'}\n`;
            dispatch(updateLastAssistantMessage({ textDelta: errorText }));
            
            // Finalize the assistant message with the error
            if (hasCreatedAssistantMessageRef.current) {
              dispatch(finalizeVoiceAssistantMessage({ audioData: undefined }));
              hasCreatedAssistantMessageRef.current = false;
            }
          } else {
            logger.log('[ChatInput] Session never started, not adding error to chat history');
          }
          
          // Show error dialog BEFORE deactivating voice mode
          setVoiceModeErrorMessage(
            error.message || 'Unable to connect to voice mode server. Please try again later.'
          );
          setShowVoiceModeError(true);
          
          // Then deactivate voice mode - reset ALL voice mode state
          setVoiceModeLoading(false);
          dispatch(setVoiceModeActive(false));
          dispatch(setVoiceModeListening(false)); // Critical: reset listening state so UI returns to normal
          dispatch(setVoiceModePaused(false));
          dispatch(setVoiceModeRestarting(false));
          hasActiveVoiceUserMessageRef.current = false;
          voiceSessionStartedRef.current = false; // Reset session started flag
        },
        onNoAudioDetected: () => {
          logger.log('[ChatInput] onNoAudioDetected callback entered. voiceModeCancelledRef:', voiceModeCancelledRef.current, '| showNoAudioDialog will be set to true if not cancelled');
          // Check if cancelled before showing dialog
          if (voiceModeCancelledRef.current) {
            logger.log('[ChatInput] onNoAudioDetected - SUPPRESSED because voiceModeCancelledRef is true');
            return;
          }
          
          logger.log('[ChatInput] onNoAudioDetected - setting showNoAudioDialog=true');
          console.warn('[ChatInput] No audio detected from microphone');
          dispatch(setShowNoAudioDialog(true));
        },
        onInactivityWarning: () => {
          // Check if cancelled before showing warning
          if (voiceModeCancelledRef.current) return;
          
          logger.log('[ChatInput] Inactivity warning triggered');
          dispatch(setShowInactivityWarning(true));
        },
        onInactivityCountdown: (secondsRemaining: number) => {
          // Update countdown in store
          if (voiceModeCancelledRef.current) return;
          
          logger.log('[ChatInput] Inactivity countdown:', secondsRemaining);
          dispatch(setInactivityCountdown(secondsRemaining));
        },
        onInactivityTimeout: () => {
          // Inactivity timeout - end voice mode
          logger.log('[ChatInput] Inactivity timeout - ending voice mode');
          
          // Guard: if voice mode is already inactive in Redux (e.g. user manually
          // stopped before the 60-second hard timeout fired), ignore this callback.
          // We use isVoiceModeActiveRef (mirrors Redux state) instead of
          // voiceSessionStartedRef because a backend socket error can reset
          // voiceSessionStartedRef to false while the UI still shows voice mode active.
          if (!isVoiceModeActiveRef.current) {
            logger.log('[ChatInput] Inactivity timeout ignored - voice mode already inactive in Redux');
            return;
          }
          
          // Clean up voice mode state
          if (hasActiveVoiceUserMessageRef.current) {
            dispatch(finalizeVoiceUserMessage({ transcript: '' }));
            hasActiveVoiceUserMessageRef.current = false;
          }
          
          if (hasCreatedAssistantMessageRef.current) {
            dispatch(finalizeVoiceAssistantMessage({ audioData: undefined }));
            hasCreatedAssistantMessageRef.current = false;
          }
          
          // Set flag to show disconnection message in the dialog
          dispatch(setDisconnectedByInactivity(true));
          
          // Make inactivity pop up appear (or stay visible) to show the disconnection message
          dispatch(setShowInactivityWarning(true));
          dispatch(setInactivityCountdown(0));
          
          // End voice mode — pass keepInactivityFlag so handleVoiceModeStop
          // does not clear the disconnectedByInactivity Redux flag we just set.
          handleVoiceModeStop({ keepDisconnectedByInactivity: true });
        },
        onPauseStateChange: (isPaused: boolean) => {
          // Sync pause state from service to Redux
          logger.log('[ChatInput] Pause state changed:', isPaused);
          dispatch(setVoiceModePaused(isPaused));
        }
      });
    } catch (error) {
      console.error('[ChatInput] Failed to start voice mode:', error);
      setVoiceModeLoading(false);
      dispatch(setVoiceModeActive(false));
      dispatch(setVoiceModeListening(false)); // Critical: reset listening state
      dispatch(setVoiceModePaused(false));
      dispatch(setVoiceModeRestarting(false));
      voiceSessionStartedRef.current = false; // Reset session started flag
      
      // Show error dialog to user
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Unable to start voice mode. Please try again later.';
      setVoiceModeErrorMessage(errorMessage);
      setShowVoiceModeError(true);
    }
  };

  // Handler for pause/resume voice mode microphone
  const handleDismissInactivityWarning = async () => {
    logger.log('[ChatInput] Dismissing inactivity warning and resuming voice mode');
    dispatch(setShowInactivityWarning(false));
    dispatch(setDisconnectedByInactivity(false));
    // Note: intentionally NOT resetting inactivityCountdown here — the tab title
    // continues counting down so the user knows time is still running out even
    // after they dismiss the dialog.
    
    // If we were disconnected by inactivity, we need to restart voice mode
    if (disconnectedByInactivity) {
      try {
        dispatch(setVoiceModeRestarting(true));
        
        // Restart voice mode from scratch
        await voiceModeService.disconnect(); // Clean disconnect first
        await startVoiceMode(); // Restart connection
        
        dispatch(setVoiceModeRestarting(false));
      } catch (error) {
        logger.error('[ChatInput] Failed to restart voice mode after dismissing warning:', error);
        dispatch(setVoiceModeRestarting(false));
        setVoiceModeErrorMessage('Failed to restart voice mode. Please try again.');
      }
    }
  };

  const handleVoiceModePauseToggle = async () => {
    if (isVoiceModePaused) {
      // Resume microphone - do NOT clear the inactivity countdown here.
      // The countdown keeps running until the backend confirms speech via speech_started.
      // DO dismiss the dialog so its backdrop no longer blocks interaction.
      dispatch(setShowInactivityWarning(false));

      try {
        dispatch(setVoiceModeRestarting(true));
        // Pause only ever stops the mic — the socket stays connected.
        // resumeAudioCapture() restarts the mic without touching the socket.
        await voiceModeService.resumeAudioCapture();
        dispatch(setVoiceModePaused(false));
        dispatch(setVoiceModeRestarting(false));
      } catch (error) {
        console.error('[VOICE MODE] Failed to resume microphone:', error);
        dispatch(setVoiceModeRestarting(false));
        // Socket dropped server-side while we were paused — end the session cleanly.
        await handleVoiceModeStop();
      }
    } else {
      // Pause microphone
      try {
        await voiceModeService.pauseAudioCapture();
        dispatch(setVoiceModePaused(true));
      } catch (error) {
        console.error('[VOICE MODE] Failed to pause microphone:', error);
      }
    }
  };

  // Handler for stopping voice mode and switching to regular chat
  const handleVoiceModeStop = async (options?: { keepDisconnectedByInactivity?: boolean }) => {
    logger.log('[VOICE MODE] Stopping voice mode and switching to regular chat');
    
    // Cancel any pending transcriptions
    dispatch(cancelTranscribingMessages());
    
    // Disconnect voice mode
    await voiceModeService.disconnect();
    dispatch(setVoiceModeActive(false));
    dispatch(setVoiceModeListening(false));
    dispatch(setVoiceModePaused(false));
    dispatch(setVoiceModeRestarting(false));
    dispatch(setShowInactivityWarning(false));
    dispatch(setInactivityCountdown(0)); // Clear countdown when manually stopping
    if (!options?.keepDisconnectedByInactivity) {
      dispatch(setDisconnectedByInactivity(false)); // Clear disconnected flag (skip when inactivity triggered the stop)
    }
    setAudioLevel(0);
    setVoiceModeLoading(false); // Ensure loading state is cleared
    voiceSessionStartedRef.current = false; // Reset session started flag
    
    // Reset refs
    hasCreatedAssistantMessageRef.current = false;
    hasActiveVoiceUserMessageRef.current = false;
    assistantResponsePendingRef.current = false;
    voiceModeCancelledRef.current = false;
    
    // ChatInput will automatically show again since isVoiceModeListening is now false
  };

  const handleSendClick = () => {
    const trimmedValue = value.trim();
    
    // If Compare mode is active, trigger comparison instead of regular chat
    if (isCompareActive && uploadedFiles.length === 2) {
      handleCompareDocuments();
      setIsCompareActive(false); // Reset after comparison
      return;
    }
    
    // DATA ANALYSIS GATING (manual send button / click path)
  if (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0) return;
    if (!trimmedValue || !onNavigateToChat || isStreaming) return;
    
    // Track first tool used when conversation starts
    if (!firstToolUsed && selectedTool) {
      setFirstToolUsed(selectedTool);
    }
    
    // Send with selected tool (if any)
    const tools = selectedTool ? [selectedTool] : undefined;
    onNavigateToChat(trimmedValue, tools);
    setValue('');
    // Don't clear selectedTool - keep it selected for next message
  };

  const handleTranslateDocuments = async () => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      logger.error('[translate] No files to translate');
      return;
    }

    if (!targetLanguages || targetLanguages.length === 0) {
      logger.error('[translate] No target languages selected');
      return;
    }

    // Check if this is a duplicate translation (same files + languages)
    const currentFileNames = uploadedFiles.map(f => f.name).sort();
    const currentLanguageCodes = targetLanguages.map(l => l.languageCode).sort();
    const user = auth.profile?.account?.username;
    
    // Compare with last translation to detect duplicates or incremental changes
    const filesAreSame = lastTranslation && 
      JSON.stringify(lastTranslation.fileNames) === JSON.stringify(currentFileNames);
    
    const isDuplicate = filesAreSame &&
      JSON.stringify(lastTranslation.languageCodes) === JSON.stringify(currentLanguageCodes);
    
    if (isDuplicate) {
      logger.warn('[translate] Duplicate translation request - same files and languages');
      return;
    }

    // Determine if we should do incremental translation (same files, new languages)
    let languagesToTranslate: ILanguageOption[];
    let isIncrementalTranslation = false;
    
    if (filesAreSame && lastTranslation) {
      // Files are the same, check if there are new languages
      const previousLanguageCodes = new Set(lastTranslation.languageCodes);
      const newLanguages = targetLanguages.filter(
        lang => !previousLanguageCodes.has(lang.languageCode)
      );
      
      if (newLanguages.length > 0) {
        // We have new languages to translate
        languagesToTranslate = newLanguages;
        isIncrementalTranslation = true;
        logger.info('[translate] Incremental translation - same files, new languages', {
          previousLanguages: lastTranslation.languageCodes,
          newLanguages: newLanguages.map(l => l.languageCode)
        });
      } else {
        // No new languages, this shouldn't happen due to isDuplicate check above
        logger.warn('[translate] No new languages found');
        return;
      }
    } else {
      // Files changed or no previous translation - translate all languages
      languagesToTranslate = targetLanguages;
      isIncrementalTranslation = false;
    }

    // Update last translation tracking in Redux
    dispatch(setLastTranslation({
      fileNames: currentFileNames,
      languageCodes: currentLanguageCodes
    }));

    try {
      translationCompletedRef.current = false;

      logger.info('[translate] Starting translation', {
        fileCount: uploadedFiles.length,
        languages: languagesToTranslate.map(l => l.languageCode),
        isIncremental: isIncrementalTranslation
      });

      const userMessage = {
          id: `translate-user-${Date.now()}`,
          role: 'user',
          content: `Translate the attached document(s) to: ${languagesToTranslate.map(l => l.label).join(', ')}`,
          timestamp: new Date().toISOString(),
        }
      
        dispatch(setTranslationHistory([userMessage])); // Start with user message in history
        dispatch(setIsTranslating(true));

      if (isIncrementalTranslation) {
        const incrementalUserMessage = {
          id: `translate-user-${Date.now()}`,
          role: 'user',
          content: `Translate the attached document(s) to additional languages: ${languagesToTranslate.map(l => l.label).join(', ')}`,
          timestamp: new Date().toISOString(),
        }

        dispatch(setTranslationHistory([...translationHistory, incrementalUserMessage]));
      }

      // Convert uploaded files to File objects
      const files: File[] = await Promise.all(
        uploadedFiles.map(async (uploadedFile) => {
          const mimeType = uploadedFile.type || 'application/octet-stream';
          const storedData = uploadedFile.data;

          // CSV and other text-based files are stored as plain text (readAsText), not base64.
          // Binary files (docx, xlsx, pdf, etc.) are stored as base64 (readAsArrayBuffer).
          const isTextBased = /\.csv$/i.test(uploadedFile.name) || mimeType === 'text/csv' || mimeType === 'text/plain';

          let blob: Blob;
          if (isTextBased) {
            // Plain text — create blob directly from the string
            blob = new Blob([storedData], { type: mimeType });
          } else {
            // Base64-encoded binary — decode to binary first
            const byteCharacters = atob(storedData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
          }

          return new File([blob], uploadedFile.name, { type: mimeType });
        })
      );

      // Create translation service instance
      const translationService = createDocumentTranslationService(msalInstance);
      // Get language codes array (only new languages if incremental)
      const targetLanguageCodes = languagesToTranslate.map(lang => lang.languageCode);

      // Call translation service
      const result = await translationService.translateDocuments(
        files,
        targetLanguageCodes,
        glossaryFiles,
        user,
        sessionId,
        (payload) => dispatch(setTranslationQuota(payload)) // Update quota in Redux as we receive it
      );

      logger.info('[translate] Translation completed', { result });
      
      // Log the full result structure for debugging
      logger.log('[translate] Full response:', JSON.stringify(result, null, 2));
      logger.log('[translate] Response keys:', Object.keys(result));
      logger.log('[translate] Result type:', result.type);
      logger.log('[translate] Result data:', result.data);

      // Extract translated files from nested structure
      // The response can be either:
      // 1. Direct format: { translated_files: [...], error_message: "..." }
      // 2. Nested format: { data: { content: { translated_files: [...] } } }
      // 3. Streaming format: { type: "content", data: { content: { translated_files: [...] } } }
      let translatedFiles = result.translated_files || 
                           result.data?.content?.translated_files ||
                           result.content?.translated_files ||
                           [];
      
      const errorMessage = result.error_message || 
                          result.data?.content?.error_message || 
                          result.content?.error_message ||
                          result.error_data?.message;

      logger.log('[translate] Extracted translated files:', translatedFiles);
      logger.log('[translate] Extracted error message:', errorMessage);

      // Add assistant message to chat with download buttons
      if (translatedFiles && translatedFiles.length > 0) {
        logger.info('[translate] Translation completed successfully', {
          fileCount: translatedFiles.length,
          isIncremental: isIncrementalTranslation
        });
        
        // Prepare translation data for the message
        const translationData = translatedFiles.map((f: any) => ({
          filename: f.filename,
          content_base64: f.content_base64,
          language: f.language || f.language_name
        }));
        
        logger.log('[translate] Prepared translationData:', translationData);
        logger.log('[translate] translationData length:', translationData.length);
        logger.log('[translate] translationData[0]:', translationData[0]);
        
        const successMessage = isIncrementalTranslation
          ? `Additional translations completed successfully! You can now download your file(s):\n\n${translatedFiles.map((f: any) => 
              `**${f.filename}** (${f.language || f.language_name})`
            ).join(', ')}`
          : `Translation completed successfully! You can now download your file(s):\n\n${translatedFiles.map((f: any) => 
              `**${f.filename}** (${f.language || f.language_name})`
            ).join(', ')}`;
        
        const assistantMessage = {
          id: `translate-assistant-${Date.now()}`,
          timestamp: new Date().toISOString(),
          role: 'assistant',
          content: successMessage,
          translationData // Store translation data for download buttons
        };

        // Use addTranslationMessage (appends in the reducer) instead of spreading the
        // stale-closure copy of translationHistory — the async gap between the user-message
        // dispatch and this point means translationHistory captured in this closure is
        // no longer current.
        dispatch(addTranslationMessage(assistantMessage));
        
        logger.log('[translate] Created assistant message with translationData');
        logger.log('[translate] assistantMessage.translationData:', assistantMessage.translationData);
        logger.log('[translate] Full assistantMessage:', JSON.stringify(assistantMessage, null, 2));
        
        // Mark translation as completed to prevent placeholder from being added
        translationCompletedRef.current = true;
      } else {
        // Unknown response format
        logger.warn('[translate] No translated_files in response', { 
          resultKeys: Object.keys(result),
          fullResult: result 
        });
        logger.error('[translate] Unexpected response format - no translated_files or error_message');
      }
    } catch (error) {
      logger.error('[translate] Translation error', { error });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Add error message to history instead of using transient error state
      // This ensures the error persists in the conversation flow
      const errorMessage = {
        id: `translate-error-${Date.now()}`,
        role: 'assistant',
        content: `${errorMsg}`,
        error: errorMsg,
        timestamp: new Date().toISOString()
      };
      
      dispatch(addTranslationMessage(errorMessage));
      dispatch(setTranslationError('')); // Clear transient error
    } finally {
      dispatch(setIsTranslating(false));
    }
  };

  const handleCompareDocuments = async () => {
    if (uploadedFiles.length !== 2) {
      logger.log('[ChatInput] Compare requires exactly 2 files');
      return;
    }

    // Show loading overlay
    dispatch(showGlobalBlocking());

    try {
      const comparisonService = createDocumentComparisonService(msalInstance);
      
      // Helper to check if file is PPTX that needs conversion
      // Note: Only PPTX files are converted to avoid potential data loss.
      // The comparison endpoint handles PDF, DOC, DOCX, and TXT natively.
      const needsConversion = (fileName: string, fileType: string) => {
        const isPpt = /\.pptx?$/i.test(fileName) || 
                     fileType === 'application/vnd.ms-powerpoint' || 
                     fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        return isPpt;
      };

      // Check if data is base64 encoded
      const isBase64 = (str: string) => {
        try {
          return btoa(atob(str)) === str;
        } catch {
          return false;
        }
      };

      // Convert base64 or text data back to File objects
      const createFileFromData = (data: string, type: string, name: string): File => {
        let blob: Blob;
        if (isBase64(data)) {
          // Base64 encoded file (PDF, DOCX, PPTX, etc.)
          const byteCharacters = atob(data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type });
        } else {
          // Plain text (TXT files)
          blob = new Blob([data], { type });
        }
        return new File([blob], name, { type });
      };

      // Process each file - convert PPTX to PDF if needed
      const processFile = async (uploadedFile: typeof uploadedFiles[0]) => {
        let fileToCompare = createFileFromData(
          uploadedFile.data, 
          uploadedFile.type, 
          uploadedFile.name
        );

        // Convert PPTX to PDF before comparing
        // We only convert PPTX because the backend comparison endpoint natively supports
        // PDF, DOC, DOCX, and TXT. Converting unnecessarily risks data loss.
        if (needsConversion(uploadedFile.name, uploadedFile.type)) {
          logger.log('[ChatInput] Converting PPTX to PDF before comparison', uploadedFile.name);
          try {
            const apimBaseUrl = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';
            const conversionEndpoint = process.env.REACT_APP_FILE_CONVERSION_URL 
              || `${apimBaseUrl}/nav-api/api/ai/convert-to-pdf`;
            
            const converted = await convertFileToPdf(fileToCompare, conversionEndpoint, msalInstance);
            
            // Convert back to File object with PDF data
            fileToCompare = createFileFromData(converted.data, converted.type, converted.name);
            logger.log('[ChatInput] PPTX conversion successful', converted.name);
          } catch (error) {
            logger.log('[ChatInput] PPTX conversion failed, using original file', error);
            // Continue with original file if conversion fails
          }
        }

        return fileToCompare;
      };

      // Process both files (with potential conversion)
      const [file1, file2] = await Promise.all([
        processFile(uploadedFiles[0]),
        processFile(uploadedFiles[1])
      ]);

      logger.log('[ChatInput] Comparing documents', { 
        file1: file1.name, 
        file2: file2.name 
      });

      // Use PDF comparison endpoint which returns base64-encoded PDF with visual comparison
      // 
      // NOTE: We're using compareDocumentsPdf instead of compareDocuments because:
      // - compareDocumentsPdf: Returns base64 PDF with visual diff highlighting (better UX)
      // - compareDocuments: Returns JSON with text-based comparison data (for programmatic use)
      // 
      // The JSON endpoint is available in the service but not currently used in the UI.
      // It could be useful for future features like showing statistics or text-based diffs.
      const result = await comparisonService.compareDocumentsPdf(file1, file2);
      
      logger.log('[ChatInput] Comparison successful', result.metadata);

      // Convert base64 PDF to data URL and open in modal
      const pdfDataUrl = `data:application/pdf;base64,${result.pdf_base64}`;
      dispatch(openImage({ src: pdfDataUrl, type: 'pdf' }));

      // Clear the Compare active state and input after successful comparison
      setIsCompareActive(false);
      setValue('');

    } catch (error) {
      logger.log('[ChatInput] Comparison failed', error);
      // Show error to user
      const errorMessage = error instanceof Error ? error.message : 'Failed to compare documents';
      setValue(`Error: ${errorMessage}. Please try again.`);
      if (onExternalValueApplied) onExternalValueApplied();
      // Reset Compare state on error as well
      setIsCompareActive(false);
    } finally {
      dispatch(hideGlobalBlocking());
    }
  };

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const form = e.currentTarget.closest('form');
      if (form) {
        // DATA ANALYSIS GATING (keyboard Enter path)
  if (!(selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0)) {
          form.requestSubmit();
        }
      }
    }
    
    // Quick tool shortcuts (Ctrl/Cmd + number)
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
      const shortcuts: { [key: string]: string } = {
        // Removed shortcut 1 (file_upload) since uploads handled contextually.
        '2': 'web_search', 
        '3': 'reasoning',
        '4': 'image_generation',
        '5': 'data_analysis'
      };
      
      if (shortcuts[e.key]) {
        e.preventDefault();
        setSelectedTool(shortcuts[e.key]);
      }
    }
    
    // Escape to clear tool selection
    if (e.key === 'Escape' && selectedTool) {
      e.preventDefault();
      setSelectedTool(null);
      dispatch(clearTargetLanguages());
    }
  }, [selectedTool, uploadedFiles, dispatch]);
  // Memoize cursor color to prevent recalculation
  const cursorColor = React.useMemo(() => {
    if (accent !== 'none') {
      return 'var(--color-accent)';
    }
    
    // For 'none' accent, force visible cursor colors based on theme mode
    if (typeof window !== 'undefined') {
      const htmlElement = document.documentElement;
      const theme = htmlElement.getAttribute('data-theme') || '';
      
      // Check if it's a dark theme
      if (theme.startsWith('dark-')) {
        return '#9ca3af'; // Much lighter gray for dark themes (gray-400)
      } else {
        return '#374151'; // Dark gray for light themes (gray-700)
      }
    }
    
    // Fallback - use a neutral visible color
    return '#6b7280'; // gray-500 as fallback
  }, [accent]);

  // Memoize textarea styles to prevent object recreation
  const textareaStyles = React.useMemo(() => ({
    scrollbarWidth: 'thin' as const,
    scrollbarColor: '#9CA3AF transparent',
    WebkitScrollbarWidth: '6px',
    fontSize: 'var(--app-font-size)', // Respects user font size preference from theme settings
    lineHeight: '24px',
    paddingTop: (isMobile) ? '8px' : isTablet ? '10px' : '12px',
    paddingBottom: '12px', // Always use base padding - no extra space for tool
    paddingLeft: '16px', // Reduced left padding (removed plus button)
  // Increase right padding so user text never flows beneath the send (PaperPlane) and upload icons cluster.
  // Two absolutely positioned buttons: send (32px wide, right-16) and upload (32px wide, right-6) plus hover/focus halo.
  // Effective reserved horizontal zone ≈ max(16+32, 6+32) = 48px from right edge, plus breathing room and scrollbar area.
  // 140px chosen to accommodate voice mode button (especially when showing 'End' text) plus upload and send buttons.
  // When voice mode is loading (showing "Cancel" button), increase padding to prevent overlap with the wider button.
  // Cancel button has minWidth:120px + padding:8px 16px + margins = ~156px, plus upload (~44px) + send (~44px) + container offsets (~32px) = ~276px total.
    paddingRight: voiceModeLoading ? '240px' : '160px',
    caretColor: cursorColor,
    verticalAlign: 'top' as const,
    boxShadow: 'none', // Always remove shadow to eliminate seam above bubbles
    transition: 'border-color 0.2s ease-in-out, outline 0.2s ease-in-out, padding-right 0.2s ease-in-out',
  } as React.CSSProperties & {
    WebkitScrollbarWidth?: string;
  }), [cursorColor, isMobile, isTablet, voiceModeLoading]);

    // Cleanup function for cancel detection
  const cleanupCancel = React.useCallback(() => {
    logger.log('[cleanupCancel] Called - Current state:', {
      filesBeingProcessed: filesBeingProcessedRef.current,
      awaitingFileSelection,
      selectedTool
    });
    filesBeingProcessedRef.current = false;
    setAwaitingFileSelection(false);
    setShowImmediateSpinner(false);
    dispatch(hideGlobalBlocking());
    // Do NOT clear data_analysis tool when OS file picker is cancelled
    // Tool should only be disabled when FileUploadDialog is explicitly cancelled
    logger.log('[cleanupCancel] Cleanup complete');
  }, [dispatch, selectedTool, awaitingFileSelection]);

  // Window focus listener for cancel detection
  // Check if file input value changed - this is INSTANT and synchronous
  useEffect(() => {
    // Sync ref with state whenever state changes
    awaitingFileSelectionRef.current = awaitingFileSelection;
  }, [awaitingFileSelection]);
  
  // Sync drag state refs with state for event handlers
  useEffect(() => {
    isFileDragInProgressRef.current = isFileDragInProgress;
  }, [isFileDragInProgress]);
  
  useEffect(() => {
    isDraggingOverRef.current = isDraggingOver;
  }, [isDraggingOver]);

  useEffect(() => {
    const handleFocus = () => {      
      // Check the ref value (current at time of focus event)
      if (!awaitingFileSelectionRef.current) {
        logger.log('[window focus] Not awaiting file selection, ignoring');
        return;
      }
      
      // INSTANT CHECK: Did the file input value change?
      // If value is still empty, user cancelled. If value exists, files were selected.
      // Check both the regular file input and the translation-specific file input,
      // because translation mode uses translationFileInputRef (not fileInputRef) to pick files.
      const hasValue = (fileInputRef.current?.value && fileInputRef.current.value !== '')
        || (translationFileInputRef.current?.value && translationFileInputRef.current.value !== '');
      
      if (!hasValue) {
        // No value = user cancelled
        cleanupCancel();
      } else {
        // Files were selected — remove the full-screen blocking overlay immediately so
        // the user can interact with the UI (e.g. click "Click to cancel") while files
        // are being read. awaitingFileSelection stays true until processing finishes.
        dispatch(hideGlobalBlocking());
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [cleanupCancel,dispatch]); // cleanupCancel dependency needed for timeout callback

    // File category helpers
    const isImageFile = (f: File) => /\.(png|jpe?g)$/i.test(f.name) || /image\/(png|jpeg)/.test(f.type);
    const isDataFile = (f: File) => /\.csv$/i.test(f.name) || f.type === 'text/csv' || f.type === 'application/vnd.ms-excel' || f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || /\.(xls|xlsx)$/i.test(f.name);
    const isTextFile = (f: File) => {
      return /\.pdf$/i.test(f.name) || f.type === 'application/pdf' ||
             /\.docx?$/i.test(f.name) || f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || f.type === 'application/msword' ||
             /\.pptx?$/i.test(f.name) || f.type === 'application/vnd.ms-powerpoint' || f.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
             /\.msg$/i.test(f.name) || f.type === 'application/vnd.ms-outlook';
    };

    const isImageFileType = (fileType: string) => {
      return fileType === 'image/png' || fileType === 'image/jpeg' || fileType === 'image/jpg';
    }
    const isDataFileType = (fileType: string) => {
      return fileType === 'text/csv' || fileType === 'application/vnd.ms-excel' || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    const isTextDocumentType = (fileType: string) => {
      return fileType === 'application/pdf' || 
             fileType === 'application/msword' || 
             fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
             fileType === 'text/plain' ||
             fileType === 'application/vnd.ms-powerpoint' ||
             fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
             fileType === 'application/vnd.ms-outlook';
    }

    
    // Categorize existing and incoming files
    const getFileCategory = React.useCallback((f: File) => {
      if (isImageFile(f)) return 'image';
      if (isDataFile(f)) return 'data';
      if (isTextFile(f)) return 'text';
      return 'unknown';
    }, []);

    // Determine allowed file types based on existing uploaded files
    const getAllowedFileTypes = React.useMemo(() => {
      // If data analysis mode is active, only allow data files
      if (selectedTool === 'data_analysis') {
        return '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      }

      if (selectedTool === 'translation') {
        return '.txt,.html,.csv,.docx,.pptx,.pdf,.msg,.xlsx,text/plain,text/html,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-outlook';
      }

      // If no files uploaded yet, allow all types
      if (uploadedFiles.length === 0) {
        return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
      }

      // Determine the category of existing files
      const existingCategories = new Set(uploadedFiles.map(f => {
        const name = f.name;
        const type = f.type;
        const mockFile = { name, type } as File;
        return getFileCategory(mockFile);
      }));

      // If we have mixed categories or unknown, fallback to all types (shouldn't happen but safe)
      if (existingCategories.size > 1 || existingCategories.has('unknown')) {
        return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
      }

      // Get the single category
      const category = Array.from(existingCategories)[0];

      // Return accept string based on category
      if (category === 'image') {
        return '.png,.jpg,.jpeg,image/png,image/jpeg';
      } else if (category === 'data') {
        return '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (category === 'text') {
        return '.ppt,.pptx,.pdf,.txt,.doc,.docx,.msg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,application/vnd.ms-outlook';
      }

      // Fallback to all types
      return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
    }, [selectedTool, uploadedFiles, getFileCategory]);


  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    logger.log('[handleFileChange] Function entered', { timestamp: performance.now() });
    
    const filesList = Array.from(e.target.files || []);
    
    logger.log('[handleFileChange] Files array created', { fileCount: filesList.length, timestamp: performance.now() });
    
    if (filesList.length === 0) {
      // User cancelled - onChange fires with empty files array
      cleanupCancel();
      return;
    }
    
    // User selected files - mark as being processed
    filesBeingProcessedRef.current = true;
    logger.log('[handleFileChange] State after setting processed flag:', {
      filesBeingProcessed: filesBeingProcessedRef.current,
      awaitingFileSelection
    });
    
    // Spinner STAYS UP — file processing/upload work starts now

    // Previously we cleared existing files on homepage (non-chat page) selection; removed to preserve prior uploads.
    // Old files now persist until user explicitly removes or clears them, or they are invalidated by tool change (e.g., data_analysis pruning).

  // Dynamic limits for general chat: up to 2 files normally, but if ALL selected + existing are images, allow up to 5.
    const fileType = (f: File) => f.type || 'application/octet-stream';
    const isExcelCsv = fileType(filesList[0]) === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || fileType(filesList[0]) === 'application/vnd.ms-excel' || fileType(filesList[0]) === 'text/csv' || /\.csv$/i.test(filesList[0].name);
    // Only auto-switch to data_analysis if not already in translation mode (CSV/Excel work in both modes)
    if(isExcelCsv && selectedTool !== 'translation') setSelectedTool('data_analysis');
    const isTranslationMode = selectedTool === 'translation';
    const isAnalysisMode = (selectedTool === 'data_analysis' || isExcelCsv) && !isTranslationMode;
    // Route warnings to the correct state: translation gets its own dialog, others share the general one
    const warnFn = isTranslationMode
      ? (msg: string) => setTranslationWarning({ title: 'File Not Accepted', message: msg })
      : setSelectionWarning;
    
    // Use staged files if dialog is open, otherwise use uploaded files
    const existingFiles = isFileUploadDialogOpen ? stagedFiles : uploadedFiles;
    
    const existingCategories = new Set(existingFiles.map(f => {
      const name = f.name;
      const type = f.type;
      const mockFile = { name, type } as File;
      return getFileCategory(mockFile);
    }));
    
    const incomingCategories = new Set(filesList.map(getFileCategory));
    
    // Check for mixing across the three main categories (image, data, text)
    // Skip category checking for translation mode - it has its own file type restrictions
    if (!isAnalysisMode && !isTranslationMode && incomingCategories.size > 1) {
      // Mixed incoming files - need to pick one category
      const categoryPriority: ('image' | 'data' | 'text')[] = ['image', 'data', 'text'];
      const chosenCategory = categoryPriority.find(cat => incomingCategories.has(cat)) || 'image';
      
      const kept = filesList.filter(f => getFileCategory(f) === chosenCategory);
      const discarded = filesList.filter(f => getFileCategory(f) !== chosenCategory);
      
      const discardedCategoriesSet = new Set(discarded.map(getFileCategory));
      const discardedCategoryNames = Array.from(discardedCategoriesSet).filter(c => c !== 'unknown').join(' and ');
      
      warnFn(`Cannot mix file types. Keeping ${chosenCategory} file(s): ${kept.map(f=>f.name).join(', ')}. Discarded ${discardedCategoryNames} file(s): ${discarded.map(f=>f.name).join(', ')}`);
      filesList.splice(0, filesList.length, ...kept);
    } else if (!isAnalysisMode && !isTranslationMode && existingCategories.size > 0 && incomingCategories.size > 0) {
      // Check if incoming category conflicts with existing category
      const existingCategory = Array.from(existingCategories)[0]; // Should only be one category
      const incomingCategory = Array.from(incomingCategories)[0];
      
      if (existingCategory !== incomingCategory && existingCategory !== 'unknown' && incomingCategory !== 'unknown') {
        const discardedNames = filesList.map(f=>f.name).join(', ');
        warnFn(`Cannot mix ${incomingCategory} file(s) with existing ${existingCategory} file(s). Discarded: ${discardedNames}`);
        if (fileInputRef.current) fileInputRef.current.value='';
        return; // Discard all incoming files
      }
    }

    const allImagesCombined = (!isAnalysisMode && !isTranslationMode) && ((existingCategories.has('image') || existingFiles.length === 0) && filesList.length > 0 && filesList.every(isImageFile));
    const MAX_TOTAL = isTranslationMode ? 5 : isAnalysisMode ? 2 : (allImagesCombined ? 5 : 2);
    const alreadyCount = existingFiles.length;
    const remainingSlots = MAX_TOTAL - alreadyCount;
    let effectiveFiles: File[] = filesList;
    if (remainingSlots <= 0) {
      if (isTranslationMode) {
        setTranslationWarning({ title: 'Maximum Files Reached', subtitle: 'One or more files could not be added.', message: 'You have reached the maximum of 5 files for translation. Remove a file to add another.' });
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
      } else {
        setSelectionWarningTitle('Maximum Files Reached');
        warnFn(isAnalysisMode
          ? 'Maximum of 2 analysis files reached. Remove one to add another.'
          : allImagesCombined
            ? 'Maximum of 5 image files reached. Remove one to add another.'
            : 'Maximum of 2 files reached. Remove one to add another.'
        );
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (filesList.length > remainingSlots) {
      const keptList = filesList.slice(0, remainingSlots).map(f => `- ${f.name}`).join('\n');
      const discardedList = filesList.slice(remainingSlots).map(f => `- ${f.name}`).join('\n');
      if (isTranslationMode) {
        setTranslationWarning({ title: 'Maximum Files Reached', subtitle: 'One or more files could not be added.', message: `Only ${MAX_TOTAL} files are allowed for translation.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}` });
      } else {
        setSelectionWarningTitle('Maximum Files Reached');
        warnFn(isAnalysisMode
          ? `Only ${MAX_TOTAL} files are allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`
          : allImagesCombined
            ? `Only ${MAX_TOTAL} image files allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`
            : `Only ${MAX_TOTAL} files allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`);
      }
      effectiveFiles = filesList.slice(0, remainingSlots);
    }

    // Allowed types differ by mode
    const analysisMime = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const translationMime = [
      'text/plain',
      'text/html',
      'text/csv',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-outlook'
    ];
    const generalMime = [
      'image/png', 'image/jpeg',
      'text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword',
      'application/vnd.ms-outlook'
    ];

    const MAX_IMAGE_BYTES = MAX_MB * 1024 * 1024; // 5MB per image file
    const MAX_OTHER_BYTES = 16 * 1024 * 1024; // 16MB for non-image files

    const readPromises: Promise<{ name: string; type: string; data: string } | null>[] = effectiveFiles.map(file => {
      return new Promise(resolve => {
        // Basic validations per file - check both type and size, report all errors
        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel';
        const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
        const isImage = /image\/(png|jpeg)/.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
        const isPpt = /\.pptx?$/i.test(file.name) || file.type === 'application/vnd.ms-powerpoint' || file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        const isDoc = /\.docx?$/i.test(file.name) || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const isTxt = /\.txt$/i.test(file.name) || file.type === 'text/plain';
        const isHtml = /\.html?$/i.test(file.name) || file.type === 'text/html';
        const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
        const isMsg = /\.msg$/i.test(file.name) || file.type === 'application/vnd.ms-outlook';

        const allowedSet = isAnalysisMode ? analysisMime : isTranslationMode ? translationMime : generalMime;
        const mimeAllowed = allowedSet.includes(file.type);
        const extAllowedGeneral = isImage || isCsv || isExcel || isPpt || isDoc || isTxt || isPdf || isMsg;
        const extAllowedAnalysis = isCsv || isExcel;
        const extAllowedTranslation = isTxt || isHtml || isCsv || isDoc || isPpt || isExcel || isMsg;
        const extAllowed = isAnalysisMode ? extAllowedAnalysis : isTranslationMode ? extAllowedTranslation : extAllowedGeneral;
        const isTypeValid = mimeAllowed || extAllowed;
        
        const isImageStrict = /image\/(png|jpeg)/.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
        const TRANSLATION_MAX_BYTES = 10 * 1024 * 1024; // 10MB limit for translation files
        const sizeLimit = isImageStrict ? MAX_IMAGE_BYTES : isTranslationMode ? TRANSLATION_MAX_BYTES : MAX_OTHER_BYTES;
        const isSizeValid = file.size <= sizeLimit;
        
        // Collect all errors for this file
        const errors: string[] = [];
        
        if (!isTypeValid) {
          errors.push(isAnalysisMode ? 'Unsupported type (CSV/Excel only)' : 'Unsupported file type');
        }
        
        if (!isSizeValid) {
          const humanLimit = isImageStrict ? MAX_MB + 'MB image limit' : isTranslationMode ? '10MB limit' : '16MB limit';
          errors.push(isTranslationMode ? `exceeds the 10MB file size limit` : `Exceeds ${humanLimit}`);
        }
        
        // If any errors, report them all and skip this file
        if (errors.length > 0) {
          console.warn('[upload] rejected_file', { name: file.name, size: file.size, type: file.type, errors });
          if (isTranslationMode) {
            const hasTypeError = !isTypeValid;
            const hasSizeError = !isSizeValid;
            let errorTitle: string;
            let errorMessage: string;
            if (hasTypeError && hasSizeError) {
              errorTitle = 'File Not Accepted';
              errorMessage = `"${file.name}" has an unsupported format and exceeds the 10MB size limit.\nAccepted formats: .txt, .pdf, .csv, .docx, .pptx, .xlsx, .msg`;
            } else if (hasTypeError) {
              errorTitle = 'File Not Accepted';
              errorMessage = `"${file.name}" has an unsupported file format.\nAccepted formats: .txt, .pdf, .csv, .docx, .pptx, .xlsx, .msg`;
            } else {
              errorTitle = 'File Too Large';
              errorMessage = `"${file.name}" exceeds the 10MB file size limit. Please upload a smaller file.`;
            }
            setTranslationWarning({ title: errorTitle, message: errorMessage });
            // Immediately clear the awaiting state so the cancel button never appears
            // alongside the warning dialog (which would cover it at zIndex 19000).
            setAwaitingFileSelection(false);
            filesBeingProcessedRef.current = false;
            return resolve(null);
          }
          const hasTypeError = !isTypeValid;
          const hasSizeError = !isSizeValid;
          const errorMessage = `${file.name}: ${errors.join(' and ')}`;
          if (hasSizeError && !hasTypeError) {
            setSelectionWarningTitle(isImageStrict ? 'Image Too Large' : 'File Too Large');
          }
          setSelectionWarning(prev => prev ? prev + `\n\n${errorMessage}` : errorMessage);
          return resolve(null);
        }
        const isDuplicate = existingFiles.some(f => f.name === file.name && f.lastModified === file.lastModified);
        if (isDuplicate) {
          console.warn('Duplicate file skipped.', file.name);
          if (isTranslationMode) {
            setTranslationWarning({ title: 'File Not Accepted', message: `"${file.name}" has already been added.` });
            setAwaitingFileSelection(false);
            filesBeingProcessedRef.current = false;
          } else {
            const dupMessage = `Duplicate file skipped: ${file.name}`;
            setSelectionWarning(prev => prev ? prev + ` | ${dupMessage}` : dupMessage);
          }
          return resolve(null);
        }
        const uniqueKey = `${file.name}_${file.lastModified}_${Math.random().toString(36).substr(2, 9)}`;
        const reader = new FileReader();
        
        logger.log('[FileReader] About to start reading file', { fileName: file.name, fileType: file.type, timestamp: performance.now() });
        
        reader.onload = () => {
          logger.log('[FileReader] File read complete', { fileName: file.name, timestamp: performance.now() });
          
          const result = reader.result;
          let dataString = '';
          if (typeof result === 'string') {
            // For images readAsDataURL would give full data URL; we prefer raw base64 payload like existing logic.
            // If result starts with data: prefix, strip metadata to keep consistency.
            if (result.startsWith('data:')) {
              const commaIdx = result.indexOf(',');
              dataString = commaIdx >= 0 ? result.substring(commaIdx + 1) : result; // strip header
            } else {
              dataString = result;
            }
          } else if (result instanceof ArrayBuffer) {
            dataString = arrayBufferToBase64(result);
          }
          const prepareMeta = async () => {
            let metaFile = { name: file.name, type: file.type, data: dataString };
            
            // Convert PPTX, DOC, and TXT files to PDF using FastAPI endpoint
            // Skip conversion for analysis mode and translation mode (they need original files)
            if (!isAnalysisMode && !isTranslationMode && (isPpt || isDoc || isTxt) && !isMsg) {
              try {
                // Get FastAPI endpoint from env or use default
                const apimBaseUrl = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';
                const conversionEndpoint = process.env.REACT_APP_FILE_CONVERSION_URL 
                  || `${apimBaseUrl}/nav-api/api/ai/convert-to-pdf`;

                metaFile = await convertFileToPdf(file, conversionEndpoint, msalInstance);
              } catch (error) {
                console.error('[upload] Conversion failed, using original file:', error);
              }
            }

            // arrayBufferToBase64 is a synchronous loop over every byte (e.g. 5M iterations
            // for a 5MB PDF) that completely blocks the JS event loop. Any cancel click the
            // user made during that block is queued by the browser but not yet processed.
            // Yielding here (setTimeout 0) lets the browser flush its event queue — including
            // any pending onMouseDown cancel — before we decide whether to dispatch the file.
            await new Promise<void>(r => setTimeout(r, 0));

            // User cancelled while file was being read — do not add the file
            if (!filesBeingProcessedRef.current) {
              resolve(null);
              return;
            }

            const metaRedux = {
              name: metaFile.name,
              size: file.size,
              type: metaFile.type,
              lastModified: file.lastModified,
              data: metaFile.data,
              key: uniqueKey,
              status: 'pending' as const
            };

            // If dialog is open, add to staged files (don't add to Redux yet)
            if (isFileUploadDialogOpen) {
              setStagedFiles(prev => [...prev, metaRedux]);
            } else {
              // Normal flow - add to Redux immediately
              dispatch(addUploadedFile(metaRedux));
            }
            
            resolve(metaFile);
          };
          prepareMeta();
        };
        // Binary types (excel, image, ppt, doc, pdf, msg) read as ArrayBuffer for base64; csv, xml, txt as text
        if (isExcel || isImage || isPpt || isDoc || isPdf || isMsg) {
          logger.log('[FileReader] Starting readAsArrayBuffer (BLOCKING)', { fileName: file.name, timestamp: performance.now() });
          reader.readAsArrayBuffer(file);
        } else {
          logger.log('[FileReader] Starting readAsText', { fileName: file.name, timestamp: performance.now() });
          reader.readAsText(file);
        }
      });
    });
    Promise.all(readPromises).then(results => {
      const metas = results.filter(Boolean) as { name: string; type: string; data: string }[];
      if (metas.length > 0) {
        // Check if files are CSV/Excel (data analysis files) - this handles both explicit data_analysis mode
        // and auto-detection from BYOD upload. Use the computed isAnalysisMode from earlier in the function.
        if (isAnalysisMode) {
          // If dialog is open, files are staged and will upload when user clicks "Upload" button
          // If dialog is NOT open (drag-and-drop or direct file picker), upload immediately
          if (isFileUploadDialogOpen) {
            logger.log('[handleFileChange] Data analysis mode - files staged for dialog confirmation');
            // awaitingFileSelection stays true; will be cleared when dialog is confirmed/cancelled
          } else {
            logger.log('[handleFileChange] Data analysis mode - uploading files immediately (drag-and-drop or direct picker)');
            uploadFilesBatch(metas);
            // awaitingFileSelection will be cleared by uploadFilesBatch when complete
          }
        } else {
          // General chat: files staged locally with 'pending' status, no backend upload needed
          logger.log('[handleFileChange] General chat mode - staging locally:', metas.map(m => m.name));
          logger.log('[handleFileChange] Clearing awaiting states - files now in Redux');
          // Clear all awaiting states - files are now in Redux
          // Extended delay to ensure files are fully processed and visible before clearing waiting indicator
          setTimeout(() => {
            setAwaitingFileSelection(false);
            filesBeingProcessedRef.current = false;
            dispatch(hideGlobalBlocking());
          }, 1000);
        }
      } else {
        // No valid files after filtering (all invalid); clear waiting indicator.
        logger.log('[handleFileChange] No valid files - clearing all states');
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      }
    });
  };

  // Paste handler: treat pasted images like a lightweight general chat upload (no spinner / picker overlay).
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
  try {
    if (isStreaming) return;

    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;

    const itemsArray = Array.from(items);

    const imageItems = itemsArray.filter(item => item.type?.startsWith("image/"));
    const textItems = itemsArray.filter(
      item => item.type === "text/plain" || item.type === "text/rtf"
    );

    // Only process images if there is at least one image AND no real text
    if (imageItems.length === 0 || textItems.length > 0) return;

    // Block image pastes when file upload is disabled (RAG modes, 3M Knowledge)
    if (shouldHideTools) {
      e.preventDefault();
      return;
    }

    // Data analysis mode blocks image pastes
    if (selectedTool === 'data_analysis') {
      setSelectionWarning(prev =>
        prev
          ? prev + ' | Data Analysis only supports CSV or Excel files.'
          : 'Data Analysis tool only supports CSV or Excel files. Pasted images are not allowed.'
      );
      e.preventDefault();
      return;
    }

    e.preventDefault(); // prevent default image paste

    // Convert image items to File objects
    const imageFiles: File[] = imageItems.map(item => {
      const blob = item.getAsFile();
      if (!blob) return null;

      const ext =
        item.type === 'image/png'
          ? 'png'
          : item.type === 'image/jpeg'
          ? 'jpg'
          : 'img';

      return new File(
        [blob],
        `pasted_image_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`,
        { type: item.type }
      );
    }).filter(Boolean) as File[];

    // Show confirmation dialog if needed
    if (hasMessages && selectedTool) {
      setConfirmationConfig({
        title: 'Paste Image?',
        message:
          'Pasting an image will switch to general chat mode and clear your chat history. Are you sure you want to continue?',
        onConfirm: () => {
          if (onClearChat) onClearChat();
          setSelectedTool(null);
          dispatch(clearTargetLanguages());
          manuallyClearedToolRef.current = true;

          setShowConfirmation(false);
          setConfirmationConfig(null);

          processPastedImagesFromFiles(imageFiles);
        }
      });

      setShowConfirmation(true);
      return;
    }

    processPastedImagesFromFiles(imageFiles);

  } catch (err) {
    console.warn('[paste-images] failed', err);
  }
};



  // Helper function to process pasted images from File objects
  const processPastedImagesFromFiles = (imageFiles: File[]) => {
    try {
      if (imageFiles.length === 0) return;
      
      // Validate and process each file
      const validFiles: File[] = [];
      
      for (const file of imageFiles) {
        // Disallow mixing with existing non-image files (mirror selection rules)
        const existingNonImages = uploadedFiles.some(f => !(/image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name)));
        if (existingNonImages) {
          setSelectionWarning(prev => prev ? prev + ' | Cannot paste image while non-image files attached.' : 'Cannot paste image while non-image files are already attached.');
          return; // abort entire paste batch
        }
        
        // Enforce remaining image slots (5 for pure images context)
        const existingImagesCount = uploadedFiles.filter(f => (/image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name))).length;
        const remainingSlots = 5 - existingImagesCount;
        
        if (validFiles.length >= remainingSlots) {
          setSelectionWarning(prev => prev ? prev + ' | Image limit reached (5).' : 'Image limit reached (5). Remove one to paste another.');
          break; // Stop processing more files
        }
        
        // Size gate (5MB per image)
        const MAX_IMAGE_BYTES = MAX_MB * 1024 * 1024;
        if (file.size > MAX_IMAGE_BYTES) {
          setSelectionWarning(prev => prev ? prev + ` | Pasted image too large (>` + MAX_MB + `MB): ${file.name}` : `Pasted image exceeds ` + MAX_MB + `MB and was skipped: ${file.name}`);
          continue;
        }
        
        // Duplicate check (by name)
        const isDuplicate = uploadedFiles.some(f => f.name === file.name);
        if (isDuplicate) {
          setSelectionWarning(prev => prev ? prev + ` | Duplicate pasted image skipped: ${file.name}` : `Duplicate pasted image skipped: ${file.name}`);
          continue;
        }
        
        validFiles.push(file);
      }
      
      // If no valid files after filtering, return
      if (validFiles.length === 0) return;
      
      // Read & dispatch similarly to general chat uploads (status 'pending', no immediate upload unless future tool selection requires)
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          let dataString = '';
          if (typeof result === 'string') {
            // For images readAsDataURL would give full data URL; we prefer raw base64 payload like existing logic.
            // If result starts with data: prefix, strip metadata to keep consistency.
            if (result.startsWith('data:')) {
              const commaIdx = result.indexOf(',');
              dataString = commaIdx >= 0 ? result.substring(commaIdx + 1) : result; // strip header
            } else {
              dataString = result;
            }
          } else if (result instanceof ArrayBuffer) {
            dataString = arrayBufferToBase64(result);
          }
          const uniqueKey = `${file.name}_${file.lastModified}_${Math.random().toString(36).substr(2, 9)}`;
          dispatch(addUploadedFile({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            data: dataString,
            key: uniqueKey,
            status: 'pending'
          }));
        };
        // Use ArrayBuffer to mirror original image processing path
        reader.readAsArrayBuffer(file);
      });
    } catch (err) {
      console.warn('[paste-images-processing] failed', err);
    }
  };

  const handleRemoveFile = (name: string) => {
    const doRemove = () => {
      dispatch(removeUploadedFile(name));
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Deselect the data_analysis tool when file is removed
      if (selectedTool === 'data_analysis') {
        setSelectedTool(null);
        manuallyClearedToolRef.current = true;
        // Clear history and reset to home state when file is removed
        if (onClearChat) {
          onClearChat();
        }
      }

      // Reset Compare active state when files are removed
      setIsCompareActive(false);

      setShowConfirmation(false);
      setConfirmationConfig(null);
    };

    // No messages — remove silently for all cases.
    if (!hasMessages) {
      doRemove();
      return;
    }

    // data_analysis with messages: removing disables the tool and clears chat history.
    if (selectedTool === 'data_analysis') {
      setConfirmationConfig({
        title: 'Remove File?',
        message: 'Removing this file will disable the Data Analysis tool and clear your chat history. Are you sure you want to continue?',
        onConfirm: doRemove,
      });
      setShowConfirmation(true);
      return;
    }

    // Other file types with an existing conversation: chat loses access to file content.
    setConfirmationConfig({
      title: 'Remove File?',
      message: 'Removing this file means the chat will no longer have access to its content. Are you sure you want to continue?',
      onConfirm: doRemove,
    });
    setShowConfirmation(true);
  };

  const handleClearAllFiles = () => {
    const doClear = (clearChat: boolean) => {
      if (clearChat && onClearChat) {
        onClearChat();
      }

      // Clear files, input, and target languages
      setValue('');
      dispatch(clearUploadedFiles());
      dispatch(clearTargetLanguages());
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Clear awaiting states and spinner when clearing all files
      setAwaitingFileSelection(false);
      filesBeingProcessedRef.current = false;
      dispatch(hideGlobalBlocking());

      // Reset Compare active state when all files are cleared
      setIsCompareActive(false);

      // If in data analysis mode, disable the tool since it requires files
      if (selectedTool === 'data_analysis') {
        setSelectedTool(null);
        manuallyClearedToolRef.current = true;
        dispatch(clearTranslationData());
        setIsTranslationCollapsed(false);
      }

      // Clear selected languages when clearing files in translation mode
      if (selectedTool === 'translation') {
        dispatch(clearTargetLanguages());
      }

      setShowConfirmation(false);
      setConfirmationConfig(null);
    };

    // No messages — clear silently, no dialog needed
    if (!hasMessages) {
      doClear(false);
      return;
    }

    // data_analysis with messages: clearing disables the tool and clears chat history
    if (selectedTool === 'data_analysis') {
      setConfirmationConfig({
        title: 'Clear All Files?',
        message: 'Clearing all files will disable the Data Analysis tool and clear your chat history. Are you sure you want to continue?',
        onConfirm: () => doClear(true),
      });
      setShowConfirmation(true);
      return;
    }

    // Other cases with messages: chat loses access to file content, but history is preserved
    setConfirmationConfig({
      title: 'Clear All Files?',
      message: 'Clearing all files means the chat will no longer have access to their content. Are you sure you want to continue?',
      onConfirm: () => doClear(false),
    });
    setShowConfirmation(true);
  };

  const triggerAdditionalUpload = () => {
    // Hide any existing global blocking overlay before showing dialog
    dispatch(hideGlobalBlocking());
    // Track which files exist before opening dialog (so we know which ones to keep/remove)
    setStagedFiles([]);
    // Show the custom file upload dialog instead of directly triggering file picker
    setIsFileUploadDialogOpen(true);
  };

  const handleFileUploadDialogDropzoneClick = () => {
    // When user clicks the dropzone in the dialog, trigger the actual file picker
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      
      logger.log('[handleFileUploadDialogDropzoneClick] Setting awaiting states and showing spinner');
      setAwaitingFileSelection(true);
      filesBeingProcessedRef.current = false;
      
      fileInputRef.current.click();
    }
  };

  const handleFileUploadDialogUpload = () => {
    // When user clicks "Upload" button in dialog - confirm the staged files
    logger.log('[handleFileUploadDialogUpload] Confirming upload of staged files:', stagedFiles.map(f => f.name));
    
    // Add all staged files to Redux
    stagedFiles.forEach(file => dispatch(addUploadedFile(file)));
    
    // Check if files need backend upload (data analysis mode)
    const isAnalysisMode = selectedTool === 'data_analysis';
    if (isAnalysisMode && stagedFiles.length > 0) {
      // Upload to backend
      const metas = stagedFiles.map(f => ({ name: f.name, type: f.type, data: f.data }));
      uploadFilesBatch(metas);
    }
    
    // Clear staged files and close dialog
    setStagedFiles([]);
    setIsFileUploadDialogOpen(false);
  };

  const handleFileUploadDialogClose = () => {
    // When user clicks "Cancel" button in dialog or clicks outside
    // Discard all staged files without adding them to Redux
    logger.log('[handleFileUploadDialogClose] Canceling and discarding staged files:', stagedFiles.map(f => f.name));
    
    // Clear staged files
    setStagedFiles([]);
    
    // Clear awaiting states
    setAwaitingFileSelection(false);
    filesBeingProcessedRef.current = false;
    dispatch(hideGlobalBlocking());
    
    // Disable tool when user cancels the FileUploadDialog
    // Mark as manually cleared to prevent auto-restore from initialTools
    if (selectedTool === 'data_analysis' || selectedTool === 'translation') {
      manuallyClearedToolRef.current = true;
      setSelectedTool(null);
      dispatch(clearTranslationData());
      setIsTranslationCollapsed(false);
    }
    
    setIsFileUploadDialogOpen(false);
  };

  const handleRemoveStagedFile = (fileName: string) => {
    // Remove a file from the staged files list
    logger.log('[handleRemoveStagedFile] Removing staged file:', fileName);
    setStagedFiles(prev => prev.filter(f => f.name !== fileName));
  };

  // Drag and drop handlers for ChatInput
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't allow drag and drop during streaming, global blocking (spinner), or for certain tools
    if (isStreaming || globalBlocking || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    e.dataTransfer.dropEffect = 'copy';
    
    // In translation mode, suppress the full-width ChatInput overlay — only the upload
    // column in TranslationMode should highlight (via its own isDragOverUpload state).
    if (selectedTool === 'translation') return;
    
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only clear dragging state if we're actually leaving the drop zone
    // relatedTarget is null when leaving the element entirely
    const relatedTarget = e.relatedTarget as Node | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDraggingOver(false);
      setShowImmediateSpinner(false); // Clear spinner if they cancel
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If drag-and-drop is not allowed, signal to browser that drop is not permitted
    if (isStreaming || globalBlocking || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    // Allow drops
    e.dataTransfer.dropEffect = 'copy';
    
    // In translation mode, keep isDraggingOver false — only the upload column highlights.
    if (selectedTool === 'translation') return;
    
    // Ensure overlay stays visible while dragging within the zone
    if (!isDraggingOver) setIsDraggingOver(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear file drag state immediately - user has dropped the file
    setIsFileDragInProgress(false);

    // Don't allow drag and drop during streaming, global blocking (spinner), or for certain tools
    if (isStreaming || globalBlocking || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      setIsDraggingOver(false);
      e.dataTransfer.dropEffect = 'none';
      // Show custom blocking message instead of browser security message
      setShowBlockingMessage(true);
      setTimeout(() => setShowBlockingMessage(false), 3000); // Auto-hide after 3 seconds
      return;
    }

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      logger.log('[handleDrop] Drop event triggered', { fileCount: files.length, timestamp: performance.now() });

      // Translation mode: route through translationFileInputRef so handleFileChange
      // applies the correct translation-specific validation (type, size, count).
      if (selectedTool === 'translation') {
        setIsDraggingOver(false);
        if (translationFileInputRef.current) {
          setAwaitingFileSelection(true);
          filesBeingProcessedRef.current = true;
          const dataTransfer = new DataTransfer();
          Array.from(files).forEach(file => dataTransfer.items.add(file));
          translationFileInputRef.current.files = dataTransfer.files;
          const event = new Event('change', { bubbles: true });
          translationFileInputRef.current.dispatchEvent(event);
        }
        return;
      }
      
      // Early file limit validation - check before processing
      const currentFileCount = uploadedFiles.length;
      const isAnalysisMode = selectedTool === 'data_analysis';
      const maxAllowed = isAnalysisMode ? 2 : 2; // For now, default to 2 for non-images
      
      if (currentFileCount + files.length > maxAllowed) {
        setIsDraggingOver(false);
        setSelectionWarningTitle('Maximum Files Reached');
        setSelectionWarning(`Cannot upload ${files.length} file(s). You already have ${currentFileCount} file(s) and the maximum is ${maxAllowed}.`);
        return;
      }
      
      logger.log('[handleDrop] Converting drag overlay to spinner immediately', { timestamp: performance.now() });
      
      // Keep drag overlay visible but convert it to a spinner immediately
      // isDraggingOver is ALREADY true from dragOver event, so overlay is visible
      // Just update its content to show spinner instead of "Drop files here"
      setShowImmediateSpinner(true); // This changes the overlay content to spinner
      setAwaitingFileSelection(true);
      filesBeingProcessedRef.current = true;
      
      logger.log('[handleDrop] Starting file processing immediately', { timestamp: performance.now() });
      
      // Process files synchronously - no RAF delay
      const dataTransfer = new DataTransfer();
      Array.from(files).forEach(file => dataTransfer.items.add(file));
      
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        const event = new Event('change', { bubbles: true });
        fileInputRef.current.dispatchEvent(event);
      }
      
      // Start Redux spinner in parallel (will take over later)
      dispatch(showGlobalBlocking('Processing files...'));
      
      logger.log('[handleDrop] File input event dispatched', { timestamp: performance.now() });
      
      // Hide local spinner once Redux spinner takes over
      setTimeout(() => {
        setShowImmediateSpinner(false);
        setIsDraggingOver(false); // Also clear drag overlay
      }, 150);
    } else {
      setIsDraggingOver(false);
    }
  };

  // Unified overlay logic: applies to all file uploads (data_analysis and general chat)
  useEffect(() => {
    const hasFiles = uploadedFiles.length > 0;
    const isAnalysis = selectedTool === 'data_analysis';
    
    // Use ref to check if files are being processed (handles async Redux update delay)
    const filesIncoming = filesBeingProcessedRef.current;
        
    if (!hasFiles && !awaitingFileSelection && !filesIncoming) {
      dispatch(hideGlobalBlocking());
      return;
    }
    if (uploadError) return; // error modal shows instead.
    
    const anyError = uploadedFiles.some(f => f.status === 'error');
    const allUploaded = uploadedFiles.length > 0 && uploadedFiles.every(f => f.status === 'uploaded');
    
    // For data analysis: spinner stays active from picker open through entire upload lifecycle
    // Only clear awaitingFileSelection when upload is complete or error occurs
    if (isAnalysis) {
      if (allUploaded || anyError) {
        // Only clear awaitingFileSelection when completely done or error
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      }
    } else {
      // General chat: clear awaitingFileSelection as soon as files are in Redux
      if (awaitingFileSelection && (hasFiles || filesIncoming)) {
        // Files are in Redux or incoming - clear awaiting flag
        if (hasFiles) {
          setAwaitingFileSelection(false);
          // Do NOT set filesBeingProcessedRef.current = false here.
          // For multi-file drops, File 1 hitting Redux triggers this effect, but File 2's
          // prepareMeta is still pending its setTimeout(r,0) yield. Clearing the ref here
          // causes prepareMeta to abort File 2 before it can dispatch. The Promise.all.then
          // 1000ms timeout in handleFileChange handles the cleanup after ALL files are processed.
        }
      }
    }
  }, [selectedTool, uploadedFiles, dispatch, uploadError, awaitingFileSelection]);
  
  // Get active function call from last assistant message (for voice mode spinner)
  const activeFunctionCall = React.useMemo(() => {
  if(chatModeState.mode === '3M Knowledge') {
    setSelectedTool(null);
  }
    const messages = chatModeState.mode === 'Ask Chat' 
      ? chatModeState.askChatHistory 
      : chatModeState.ragHistory;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant' && lastMessage.functionCall?.isActive) {
      return lastMessage.functionCall;
    }
    return null;
  }, [chatModeState]);

  return (
    <>
      {/* Voice Mode Circle - shown immediately when voice mode is active AND on main content page */}
      {isVoiceModeActive && chatPage && createPortal(
        <VoiceCircle
          isListening={isVoiceModeListening}
          isPaused={isVoiceModePaused}
          audioLevel={audioLevel}
          frequencyData={frequencyData}
          isRestarting={isVoiceModeRestarting}
          showInactivityWarning={showInactivityWarning}
          hasFunctionCallInProgress={activeFunctionCall !== null}
          onPauseToggle={handleVoiceModePauseToggle}
          onStop={handleVoiceModeStop}
          isDarkMode={mode === 'dark'}
        />,
        document.body
      )}
      
      {/* Chat Input - hidden when voice mode is active on main content page, instant switch */}
      {!(isVoiceModeActive && chatPage) && (
        <motion.form 
          // No padding on ChatInput itself - parent containers handle alignment
          className={`w-full mx-auto flex flex-col justify-center ${!chatPage ? 'sm:pt-0' : ''}`}
          style={{ maxWidth: '2000px' }}
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
      {/* Drag and drop overlay - wraps only the input container */}
      <div 
        className={`relative ${isDraggingOver ? 'dragging-active' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDraggingOver && (
          <div 
            className="absolute inset-0 z-[9999] flex items-center justify-center pointer-events-none"
            style={{
              backgroundColor: 'rgba(94, 149, 237, 0.1)',
              border: '3px dashed #5E95ED',
              borderRadius: '8px'
            }}
          >
            {showImmediateSpinner ? (
              // Show spinner when files dropped - instant feedback
              <div 
                style={{
                  width: 42,
                  height: 42,
                  border: '5px solid rgba(94, 149, 237, 0.25)',
                  borderTop: '5px solid #5E95ED',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
            ) : (
              // Show drop message when hovering
              <div className="flex flex-col items-center gap-2 p-4 pt-2 pb-2 rounded-lg bg-[var(--color-secondary)] shadow-xl max-w-sm" style={{ pointerEvents: 'none' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#5E95ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p className="text-base font-semibold text-[#5E95ED]">Drop files here to upload</p>
                <p className="text-xs text-[var(--color-text)] opacity-70 text-center">
                  {selectedTool === 'data_analysis' 
                    ? 'CSV or Excel files only (max 2 files, 16MB each)'
                    : 'Images, documents, or data files'}
                </p>
              </div>
            )}
          </div>
        )}

      {/* Centered error modal when uploadError active */}
      {uploadError && selectedTool && (selectedTool === 'file_upload' || selectedTool === 'data_analysis') && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          style={{ zIndex: 20000 }}
          aria-live="assertive"
          onMouseDown={(e) => {
            // Clicking backdrop closes dialog
            if (e.target === e.currentTarget) {
              logger.log('[upload-error-modal] Backdrop clicked');
              setUploadError(null);
              setSelectedTool(null);
              dispatch(clearUploadedFiles());
              dispatch(clearTargetLanguages());
              dispatch(hideGlobalBlocking());
            }
          }}
        >
          <div
            className="flex items-start gap-3 p-4 rounded-md border border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 shadow-xl max-w-md w-full mx-4"
            onMouseDown={(e) => { e.stopPropagation(); }}
          >
          <div className="flex-shrink-0" aria-hidden="true">
            {/* Simple warning icon using radial exclamation */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l9 19H3L12 2z" />
              <line x1="12" y1="10" x2="12" y2="14" />
              <circle cx="12" cy="18" r="1" />
            </svg>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-sm">Could not upload file at this time.</span>
            <span className="text-xs">{uploadError.file}: {uploadError.message}</span>
            <div className="flex gap-2 mt-1 flex-wrap">
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                onClick={(e) => {
                  e.stopPropagation();
                  if (fileInputRef.current) {
                    fileInputRef.current.value='';
                    setTimeout(()=>{
                      fileInputRef.current?.click();
                      setUploadError(null);
                    },0);
                  } else {
                    setUploadError(null);
                  }
                }}
              >Select File Again</button>
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-400 dark:hover:bg-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadError(null);
                  setSelectedTool(null);
                  dispatch(clearUploadedFiles());
                  dispatch(clearTargetLanguages());
                  dispatch(hideGlobalBlocking());
                }}
              >Close</button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
        <Label.Root htmlFor="chat-input" className="sr-only">Chat Input</Label.Root>
        <div className="relative w-full group flex flex-col">
        {selectedTool !== 'translation' && (
          <div className="relative">
            <textarea
              ref={textareaRef}
              id="chat-input"
              className={`w-full ${isTablet ? 'min-h-[64px]' : 'min-h-[56px]'} max-h-${iconSize || 32} bg-[var(--color-secondary)] text-[var(--color-text)] focus:outline-none resize-none rounded-t-lg 
              border-l border-r border-t border-[var(--color-accent)] group-focus-within:!border-l-[#5E95ED] group-focus-within:!border-r-[#5E95ED] group-focus-within:!border-t-[#5E95ED]`}
              aria-label="Chat message input"
              aria-describedby={selectedTool ? "selected-tool" : undefined}
              // placeholder styling in index.css
              style={textareaStyles}
              placeholder={getPlaceholder()}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={isStreaming}
              rows={1}
            />
          </div>
        )}
        
        {/* Unified tool & file bubble container - always visible */}
          <div 
            ref={bottomBubbleContainerRef}
            id="selected-tool"
            className={`border-l border-r border-b ${selectedTool === 'translation' ? 'border-t rounded-t-lg' : ''} border-[var(--color-accent)] bg-[var(--color-secondary)] rounded-b-lg px-3 py-2 group-focus-within:!border-l-[#5E95ED] group-focus-within:!border-r-[#5E95ED] group-focus-within:!border-b-[#5E95ED] ${selectedTool === 'translation' ? 'group-focus-within:!border-t-[#5E95ED]' : ''}`}
            style={{ boxShadow: 'none', transition: 'border-color 0.2s ease-in-out' }}
            role="group"
            aria-label="Tools and uploaded files"
          >
            {/* Translation Mode Inline UI - appears at top when translation mode is active */}
            {selectedTool === 'translation' && !isTranslationCollapsed && (
              <TranslationMode
                uploadedFiles={uploadedFiles}
                awaitingFileSelection={awaitingFileSelection}
                chatPage={chatPage}
                iconSize={iconSize}
                translationFileInputRef={translationFileInputRef}
                bottomBubbleContainerRef={bottomBubbleContainerRef}
                textareaRef={textareaRef}
                previousHeightRef={previousHeightRef}
                filesBeingProcessedRef={filesBeingProcessedRef}
                onHeightChange={onHeightChange}
                setAwaitingFileSelection={setAwaitingFileSelection}
                setSelectionWarning={setSelectionWarning}
                setTranslationWarning={setTranslationWarning}
                handleRemoveFile={handleRemoveFile}
                handleTranslateDocuments={handleTranslateDocuments}
                handleFileChange={handleFileChange}
                clearChatInputDragOverlay={() => setIsDraggingOver(false)}
              />
            )}
            
            {/* Only show tools in Ask Chat mode */}
            {!shouldHideTools && (
              <>
                {/* Mobile: Plus icon button and selected tool display */}
                {isMobile ? (
                  <div className="flex flex-nowrap justify-between gap-2 items-center mb-1">
                    {/* Plus icon button to open tools menu */}
                      <button
                      type="button"
                      onClick={() => setIsMobileToolsMenuOpen(!isMobileToolsMenuOpen)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[#5E95ED] text-[#5E95ED] hover:bg-[var(--color-accent)] hover:text-white transition-all duration-200 flex-shrink-0"
                      aria-label="Open tools menu"
                      disabled={isFileDragInProgress}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </button>

                    {/* Display selected tool if any */}
                    {selectedTool && (() => {
                      const selectedOption = chatOptions.find(opt => opt.apiLabel === selectedTool);
                      if (!selectedOption) return null;
                      const IconComponent = selectedOption.icon as React.ComponentType<any>;
                      return (
                        <div className={`flex items-center ${selectedTool === 'translation' ? 'gap-1' : ''}`}>
                          <div
                          className="inline-flex items-center gap-2 px-3 py-1.5 h-8 rounded-lg text-xs font-medium border border-[#5E95ED] bg-[var(--color-accent)] text-white"
                          onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveTool();
                            }}
                        >
                          <span className="flex items-center justify-center">
                            <IconComponent width={iconSize} />
                          </span>
                          <span className="whitespace-nowrap">{selectedOption.label}</span>
                        </div>
                        {/* Collapse/Expand icon for translation tool when active */}
                        {selectedTool === 'translation' && translationHistory.length > 0 && (
                          <div 
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#5E95ED] text-[#5E95ED] hover:text-white transition-all duration-200 flex-shrink-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsTranslationCollapsed(!isTranslationCollapsed);
                            }}
                            aria-label={isTranslationCollapsed ? 'Expand translation details' : 'Collapse translation details'}
                          >
                            {isTranslationCollapsed ? (
                              <ChevronUpIcon width={iconSize} />
                            ) : (
                              <ChevronDownIcon width={iconSize} />
                            )}
                          </div>
                        )}
                        </div>
                      );
                    })()}
                    
                  </div>
                ) : (
                  /* Desktop/Tablet: Horizontal tool bubbles */
                  <div className="flex flex-nowrap gap-2 items-center mb-1 overflow-x-auto scrollbar-hide" aria-label="Available tools">
                    {chatOptions.map(toolOption => {
                      const IconComponent = toolOption.icon as React.ComponentType<any>;
                      const isActive = selectedTool === toolOption.apiLabel;
                      
                      // Only disable image generation when it's actively streaming
                      const isImageGenDisabled = isImageStreaming && !error && toolOption.apiLabel === 'image_generation';
                      // Disable all tools when user is dragging a file over the window
                      const isDisabled = isImageGenDisabled || isFileDragInProgress;
                      
                      // Map tool API labels to tutorial data attributes
                      const getTutorialAttribute = (apiLabel: string) => {
                        if (apiLabel === 'web_search') return 'web-search-tool';
                        if (apiLabel === 'reasoning') return 'reasoning-tool';
                        if (apiLabel === 'image_generation') return 'image-generation-tool';
                        if (apiLabel === 'data_analysis') return 'data-analysis-tool';
                        if (apiLabel === 'translation') return 'document-translation-tool';
                        return undefined;
                      };
                      
                      return (
                        <button
                          key={toolOption.apiLabel}
                          type="button"
                          aria-pressed={isActive}
                          disabled={isDisabled}
                          aria-label={isActive ? `${toolOption.label} selected` : `Activate ${toolOption.label}`}
                          data-tutorial={getTutorialAttribute(toolOption.apiLabel)}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isDisabled) return;
                            if (isActive) {
                              handleRemoveTool();
                            } else {
                              handleOptionClick(toolOption.apiLabel, e as any);
                            }
                          }}
                          className={`tool-button group relative inline-flex items-center gap-0 px-2 py-1 h-8 rounded-lg text-xs font-medium border transition-all duration-300 ${isActive ? 'overflow-visible' : 'overflow-hidden'}
                            focus:outline-none flex-shrink-0 ${isActive ? 'tool-button-active' : 'tool-button-inactive'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${isActive ? 'cursor-pointer' : ''} ${isMobile || isTablet ? 'tool-button-mobile-tablet' : 'tool-button-desktop'}`}
                          style={{ 
                            lineHeight: '1',
                          }}
                        >
                          <span className="flex items-center justify-center" style={{ minWidth: '18px', height: '18px', lineHeight: '1' }}>
                            <IconComponent width={18} height={18} size={18} style={{ display: 'block' }} />
                          </span>
                          {/* Text label - always present for active buttons, shows on hover for desktop inactive buttons */}
                          <span 
                            className="whitespace-nowrap overflow-hidden"
                            style={{
                              maxWidth: isActive ? '200px' : '0px',
                              opacity: isActive ? 1 : 0,
                              paddingLeft: isActive ? '6px' : '0px',
                              transition: 'max-width 300ms ease-in-out, opacity 300ms ease-in-out, padding-left 300ms ease-in-out',
                              height: '18px',
                              lineHeight: '18px',
                              display: 'inline-block',
                              position: 'relative',
                              top: '0.075rem', // visually center with icon, but allows text to expand without affecting button height
                            }}
                            data-hover-text="true"
                          >
                            {toolOption.label}{isDisabled && toolOption.apiLabel === 'image_generation' && ' (Generating...)'}
                          </span>
                        
                          {/* X button for active tools - positioned on right */}
                          {isActive && (
                            <span 
                              className="flex items-center justify-center cursor-pointer"
                              style={{ height: '18px', lineHeight: '1', paddingLeft: '12px', paddingRight: '4px', opacity: 1, paddingTop: '0.05rem' }}
                              data-hover-x="true"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveTool();
                              }}
                            >
                              <Cross2Icon width={14} height={14} style={{ display: 'block', flexShrink: 0 }} />
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {selectedTool === 'translation' && translationHistory.length > 0 &&  (
                          <div 
                            className="ml-auto cursor-pointer inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#5E95ED] text-[#5E95ED] hover:text-white transition-all duration-200 flex-shrink-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsTranslationCollapsed(!isTranslationCollapsed);
                            }}
                            aria-label={isTranslationCollapsed ? 'Expand translation details' : 'Collapse translation details'}
                          >
                            {isTranslationCollapsed ? (
                              <ChevronUpIcon width={iconSize} />
                            ) : (
                              <ChevronDownIcon width={iconSize} />
                            )}
                          </div>
                        )}
                  </div>
                )}
              </>
            )}
            
            {/* Collapsible file preview section - only when files are uploaded AND NOT in translation mode */}
            {uploadedFiles.length > 0 && selectedTool !== 'translation' && (
              <div className="flex flex-col gap-2">
                {/* Collapse/Expand header - only show on chatPage */}
                {chatPage && (
                  <button
                    type="button"
                    onClick={() => setIsFilePreviewCollapsed(!isFilePreviewCollapsed)}
                    className="flex items-center gap-1 text-xs text-[var(--color-text)] opacity-70 hover:opacity-100 transition-opacity self-start"
                    aria-label={isFilePreviewCollapsed ? 'Expand file previews' : 'Collapse file previews'}
                    aria-expanded={!isFilePreviewCollapsed}
                  >
                    {isFilePreviewCollapsed ? (
                      <>
                        <ChevronDownIcon width={16} height={16} className="flex-shrink-0" />
                        <span className="translate-y-[2px]">{uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} attached</span>
                      </>
                    ) : (
                      <>
                        <ChevronUpIcon width={16} height={16} className="flex-shrink-0" />
                        <span className="translate-y-[2px]">Hide files</span>
                      </>
                    )}
                  </button>
                )}
                {/* File preview content */}
                {!isFilePreviewCollapsed && (
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Uploaded file representations: image thumbnails or generic file bubbles */}
                    {uploadedFiles.map(f => {
                      const isImage = /image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name);
                if (isImage) {
                  const mime = f.type.includes('jpeg') ? 'image/jpeg' : 'image/png';
                  const dataUrl = `data:${mime};base64,${f.data}`;
                  return (
                    <div
                      key={f.key || f.name}
                      className="relative w-12 max-w-16 h-12 rounded-md overflow-hidden border border-[#5E95ED] bg-[#202024] dark:bg-[#202024] light:bg-[#E8F3FF] flex items-center justify-center shadow-sm cursor-pointer group"
                      style={{
                        backgroundColor: document.documentElement.getAttribute('data-theme')?.startsWith('dark-') ? '#202024' : '#E8F3FF'
                      }}
                      aria-label={`Image file ${f.name}`}
                      onClick={() => {
                        // Open full screen modal using existing image modal infrastructure
                        try {
                          dispatch({ type: 'chat/openImage', payload: dataUrl });
                        } catch (err) {
                          console.warn('[thumbnail] failed to dispatch openImage', err);
                        }
                      }}
                    >
                      <img
                        src={dataUrl}
                        alt={f.name}
                        className="object-cover w-full h-full group-hover:opacity-90"
                        style={{ imageRendering: 'auto' }}
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemoveFile(f.name); }}
                        aria-label={`Remove image ${f.name}`}
                        className="absolute top-0 right-0 m-0.5 bg-black/40 hover:bg-black/60 text-white rounded-full p-[2px] leading-none flex items-center justify-center"
                        style={{ lineHeight: 0 }}
                      >
                        <Cross2Icon width={12} height={12} />
                      </button>
                    </div>
                  );
                }
                return (
                  <div 
                    key={f.key || f.name} 
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-[#5E95ED] text-[#5E95ED] shadow-sm" 
                    style={{
                      backgroundColor: document.documentElement.getAttribute('data-theme')?.startsWith('dark-') ? '#202024' : '#E8F3FF'
                    }}
                    aria-label={`File ${f.name}`}
                    ref={(el) => {
                      if (el) {
                        // Only add title if text is truncated
                        const span = el.querySelector('span.truncate');
                        if (span && span.scrollWidth > span.clientWidth) {
                          el.setAttribute('title', f.name);
                        } else {
                          el.removeAttribute('title');
                        }
                      }
                    }}
                  >
                    <span className="flex items-center justify-center flex-shrink-0" style={{ height: '14px' }}>
                      <FileIcon width={iconSize || 14} height={iconSize || 14} />
                    </span>
                    <span 
                      className="truncate leading-none" 
                      style={{ maxWidth: `${toolTextWidth}px`, paddingTop: '1px' }}
                    >
                      {f.name}
                    </span>
                    <button type="button" onClick={() => handleRemoveFile(f.name)} className="p-0.5 rounded-full transition-colors flex-shrink-0 hover:bg-[#5E95ED] hover:text-white flex items-center justify-center" aria-label={`Remove uploaded file ${f.name}`}>
                      <Cross2Icon width={12} height={12} />
                    </button>
                  </div>
                );
              })}
              {/* Action buttons for uploaded files */}
              {uploadedFiles.length >= 1 && (
                <button 
                  type="button" 
                  disabled={isStreaming}
                  className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded bg-[var(--color-accent)] text-white hover:opacity-80 whitespace-nowrap min-w-fit leading-none" 
                  style={{ paddingTop: '5px', paddingBottom: '5px' }}
                  onClick={() => {
                    if (isImageFileType(uploadedFiles[0].type)) {
                      setValue('Describe the image by providing a detailed analysis and highlighting the most interesting aspects without making assumptions. Give the result in Markdown format. Return the result in a summary and each component separately, delivering a structured output of its content.');
                      if (onExternalValueApplied) onExternalValueApplied();
                    } else if (isDataFileType(uploadedFiles[0].type)) {
                      setValue('Analyze the data in the file.');
                      if (onExternalValueApplied) onExternalValueApplied();
                    } else {
                      setValue('Summarize the content.');
                      if (onExternalValueApplied) onExternalValueApplied();
                    }
                  }}
                >
                  {isImageFileType(uploadedFiles[0].type) ? 'Describe' : isDataFileType(uploadedFiles[0].type) ? 'Analyze' : 'Summarize'}
                </button>

              )}
              {/* Compare button - only show when exactly 2 text documents are uploaded */}
              {uploadedFiles.length === 2 && uploadedFiles.every(f => isTextDocumentType(f.type)) && (
                <button 
                  type="button" 
                  disabled={isStreaming}
                  className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded whitespace-nowrap min-w-fit leading-none transition-all" 
                  style={{ 
                    paddingTop: '5px', 
                    paddingBottom: '5px',
                    backgroundColor: isCompareActive ? '#5E95ED' : 'transparent',
                    color: isCompareActive ? 'white' : '#5E95ED',
                    border: isCompareActive ? '2px solid #5E95ED' : '2px solid #5E95ED',
                    opacity: isCompareActive ? 1 : 0.9,
                    fontWeight: isCompareActive ? '600' : '500'
                  }}

                  onClick={() => {
                    // Toggle Compare mode
                    if (isCompareActive) {
                      // Deactivate: clear prompt and reset state
                      setIsCompareActive(false);
                      setValue('');
                    } else {
                      // Activate: set prompt and mark as active
                      setIsCompareActive(true);
                      setValue('Compare these two documents and highlight the key differences.');
                      if (onExternalValueApplied) onExternalValueApplied();
                    }
                  }}
                  aria-label={isCompareActive ? "Cancel comparison" : "Compare documents"}
                >
                  {isCompareActive ? '✓ Compare' : 'Compare'}
                </button>
              )}
              
              {uploadedFiles.length > 1 && (
                <>
                  <button type="button" className="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded bg-[var(--color-accent)] text-white hover:opacity-80 whitespace-nowrap leading-none" style={{ paddingTop: '5px', paddingBottom: '5px' }} onClick={handleClearAllFiles} aria-label="Clear all files">Clear</button>
                </>
              )}
                  </div>
                )}
              </div>
            )}
          </div>
          
        {/* Action buttons container - positioned on the right side with proper spacing */}
        {selectedTool !== 'translation' && (<div className="absolute right-4 flex items-center gap-0 pl-4 top-2">
          {/* Voice Mode button - only visible in Ask Chat mode */}
          {!shouldHideTools && (
            <button
              type="button"
              onClick={async (e) => { 
                e.preventDefault(); 
                e.stopPropagation();
                
                if (voiceModeLoading) {
                  // Cancel initialization - set flag to prevent callbacks from proceeding
                  logger.log('[ChatInput] Canceling voice mode initialization');
                  voiceModeCancelledRef.current = true;
                  setVoiceModeLoading(false);
                  dispatch(setVoiceModeActive(false));
                  dispatch(setVoiceModeListening(false));
                  hasCreatedAssistantMessageRef.current = false;
                  hasActiveVoiceUserMessageRef.current = false;
                  assistantResponsePendingRef.current = false;
                  await voiceModeService.disconnect();
                  return;
                }
                
                if (isVoiceModeActive) {
                  // End voice mode
                  logger.log('[ChatInput] Ending voice mode');
                  voiceModeCancelledRef.current = false; // Reset cancel flag so next session works
                  dispatch(setVoiceModeActive(false));
                  dispatch(setVoiceModeListening(false));
                  hasCreatedAssistantMessageRef.current = false;
                  hasActiveVoiceUserMessageRef.current = false;
                  assistantResponsePendingRef.current = false;
                  await voiceModeService.disconnect();
                  return;
                }
                
                // Start voice mode initialization
                logger.log('[ChatInput] Voice mode button clicked, starting initialization');
                
                // Check if permission is explicitly denied
                const permissionState = await checkMicrophonePermission();
                logger.log('[ChatInput] Microphone permission state:', permissionState);
                
                if (permissionState === 'denied') {
                  // Only show dialog if explicitly denied - user needs to manually enable
                  setShowMicPermissionDialog(true);
                  return;
                }
                
                // For 'prompt', 'granted', or 'unsupported' - just start voice mode
                // The browser will prompt for permission if needed when we access microphone
                startVoiceMode();
              }}
              className="border-none transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={(isStreaming && !voiceModeLoading && !isVoiceModeActive) || (selectedTool != null)}
              aria-label={voiceModeLoading ? "Cancel voice mode" : isVoiceModeActive ? "End voice mode" : "Start voice mode"}
              title={voiceModeLoading ? "Cancel voice mode" : isVoiceModeActive ? "End voice mode" : isStreaming ? "Voice mode unavailable while responding" : "Start voice mode"}
              data-tutorial="voice-mode-button"
              onMouseEnter={(e) => {
                if (!voiceModeLoading && !isVoiceModeActive && !isStreaming) {
                  e.currentTarget.style.backgroundColor = '#6B7280';
                }
              }}
              onMouseLeave={(e) => {
                if (!voiceModeLoading && !isVoiceModeActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              style={{
                width: (voiceModeLoading || isVoiceModeActive) ? 'auto' : 'auto',
                height: 'auto',
                minWidth: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? 'auto' : '120px') : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? 0 : '8px') : 0,
                lineHeight: 0,
                padding: (voiceModeLoading || isVoiceModeActive) ? (isMobile ? '8px' : '8px 16px') : '8px',
                backgroundColor: (voiceModeLoading || isVoiceModeActive) ? 'var(--color-accent)' : 'transparent',
                color: (voiceModeLoading || isVoiceModeActive) ? 'white' : 'var(--color-text)',
                transition: 'background-color 0.2s ease',
                marginLeft: '2px',
                marginRight: '2px',
                borderRadius: '8px',
              }}
            >
              {voiceModeLoading ? (
                <>
                  <div
                    className="animate-spin rounded-full border-2 border-white border-t-transparent"
                    style={{ width: 16, height: 16}}
                  />
                  {!isMobile && (
                    <span className="text-sm font-medium" style={{ paddingTop: '2px', paddingLeft: '2px' }}>
                      Cancel
                    </span>
                  )}
                </>
              ) : isVoiceModeActive ? (
                <>
                  <AudioWaveform 
                    isActive={isVoiceModeActive} 
                    audioLevel={audioLevel}
                    frequencyData={frequencyData}
                    size={16}
                    color="white"
                  />
                  <span className="text-sm font-medium">End</span>
                </>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AudioLines size={iconSize || 24} />
                </span>
              )}
            </button>
          )}

          {/* Upload button - only visible in Ask Chat mode */}
          {!shouldHideTools && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); triggerAdditionalUpload(); }}
              className="border-none transition text-[var(--color-text)] 
              disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={(selectedTool === 'data_analysis' ? uploadedFiles.length >= 2 : (uploadedFiles.every(f => /\.(png|jpe?g)$/i.test(f.name) || /image\/(png|jpeg)/.test(f.type)) ? uploadedFiles.length >= 5 : uploadedFiles.length >= 2)) || isStreaming || ['image_generation','web_search','reasoning'].includes(selectedTool || '')}
              aria-label={(selectedTool === 'data_analysis' ? uploadedFiles.length >= 2 : (uploadedFiles.every(f => /\.(png|jpe?g)$/i.test(f.name) || /image\/(png|jpeg)/.test(f.type)) ? uploadedFiles.length >= 5 : uploadedFiles.length >= 2)) ? 'File limit reached' : 'Upload another file'}
              title={(selectedTool === 'data_analysis' ? uploadedFiles.length >= 2 : (uploadedFiles.every(f => /\.(png|jpe?g)$/i.test(f.name) || /image\/(png|jpeg)/.test(f.type)) ? uploadedFiles.length >= 5 : uploadedFiles.length >= 2)) ? 'File limit reached' : isStreaming ? 'Cannot upload while responding' : ['image_generation','web_search','reasoning'].includes(selectedTool || '') ? 'File upload not available for this tool' : 'Upload file'}
              data-tutorial="byod-button"
              onMouseEnter={(e) => {
                if (!(selectedTool === 'data_analysis' ? uploadedFiles.length >= 2 : (uploadedFiles.every(f => /\.(png|jpe?g)$/i.test(f.name) || /image\/(png|jpeg)/.test(f.type)) ? uploadedFiles.length >= 5 : uploadedFiles.length >= 2)) && !isStreaming && !['image_generation','web_search','reasoning'].includes(selectedTool || '')) {
                  e.currentTarget.style.backgroundColor = '#6B7280';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              style={{
                width: 'auto',
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 0,
                padding: '8px',
                marginLeft: '2px',
                marginRight: '2px',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <UploadIcon width={iconSize || 24} height={iconSize || 24} />
              </span>
            </button>
          )}

          {/* Hidden file input for actually uploading the file */}
          <input
            data-testid="chat-file-input"
            ref={fileInputRef}
            type="file"
            multiple
            // Dynamic accept: restricts to same file type as already uploaded files
            accept={getAllowedFileTypes}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '1px',
              height: '1px',
              opacity: 0.01,
            }}
            onClick={() => {
              filesBeingProcessedRef.current = false;
              
              // Show spinner immediately
              setAwaitingFileSelection(true);
            }}
            onChange={handleFileChange}
          />

          {/* Send/Stop button - always visible */}
          <button
            type="button"
            onClick={() => {
              logger.log('[ChatInput] Stop/Send button clicked', { isStreaming, hasOnStopStream: !!onStopStream });
              if (isStreaming) {
                logger.log('[ChatInput] Calling onStopStream');
                onStopStream?.();
              } else {
                handleSendClick();
              }
            }}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => {
              if (!(!value.trim() || (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0))) {
                e.currentTarget.style.backgroundColor = '#4a7fd9';
              }
            }}
            onMouseLeave={(e) => {
              if (!(!value.trim() || (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0))) {
                e.currentTarget.style.backgroundColor = '#5E95ED';
              }
            }}
            className="border-none transition text-[var(--color-text)]"
            disabled={!isStreaming && (!value.trim() || (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0))}
            aria-label={isStreaming ? "Stop" : "Send"}
            style={{
              width: 'auto',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: (!isStreaming && (!value.trim() || (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0))) ? 0.5 : 1,
              marginLeft: '4px',
              marginRight: '4px',
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: (!value.trim() || (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0)) ? 'transparent' : '#5E95ED',
              transition: 'background-color 0.2s ease',
            }}
          >
            {(isStreaming && !isImageStreaming) ? (
              <span style={{ width: iconSize || 24, height:  iconSize || 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <StopIcon size={iconSize || 24} color="#D10411" />
              </span>
            ) : isStreaming ? ( <></> ) : (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PaperPlaneIcon
                  className="transition-colors"
                  width={iconSize || 20}
                  height={iconSize || 20}
                />
              </span>
            )}
          </button>
        </div>)}

          {/* Selection warning lightweight modal */}
          {selectionWarning && createPortal(
            <div
              className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
              style={{ zIndex: 19000 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) { setSelectionWarning(null); setSelectionWarningTitle(null); }
              }}
            >
              <div
                className="flex flex-col gap-4 p-6 rounded-lg border border-[var(--color-accent)] bg-[var(--color-secondary)] text-[var(--color-text)] shadow-xl max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
                role="alert"
                aria-live="assertive"
                aria-label="File selection warning"
              >
                {/* Header */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <circle cx="12" cy="16" r="1" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-1">{selectionWarningTitle ?? 'File Selection Warning'}</h3>
                    <p className="text-sm opacity-80">One or more files could not be added.</p>
                  </div>
                </div>

                {/* Theme-aware warning message box */}
                <div
                  className={`p-3 rounded text-sm whitespace-pre-line ${mode === 'dark' ? '' : 'bg-red-100 text-red-800'}`}
                  style={mode === 'dark' ? { backgroundColor: 'rgba(127, 29, 29, 0.3)', color: '#fecaca' } : undefined}
                >
                  {selectionWarning}
                </div>

                {/* Action button */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm rounded bg-[var(--color-accent)] text-white hover:opacity-80"
                    onClick={() => {
                      setSelectionWarning(null);
                      setSelectionWarningTitle(null);
                      // If in data_analysis mode with no files after invalid removal, now open dialog
                      if (selectedTool === 'data_analysis' && uploadedFiles.length === 0) {
                        setIsFileUploadDialogOpen(true);
                      }
                    }}
                  >Got It</button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Translation-specific file warning dialog — isolated from the general selectionWarning */}
          {translationWarning && createPortal(
            <div
              className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
              style={{ zIndex: 19000 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setTranslationWarning(null);
              }}
            >
              <div
                className="flex flex-col gap-4 p-6 rounded-lg border border-[var(--color-accent)] bg-[var(--color-secondary)] text-[var(--color-text)] shadow-xl max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
                role="alert"
                aria-live="assertive"
                aria-label="Translation file warning"
              >
                {/* Header */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <circle cx="12" cy="16" r="1" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-1">{translationWarning.title}</h3>
                    <p className="text-sm opacity-80">{translationWarning.subtitle ?? 'The selected file could not be added to your translation.'}</p>
                  </div>
                </div>

                {/* Theme-aware warning message box */}
                <div
                  className={`p-3 rounded text-sm whitespace-pre-line ${mode === 'dark' ? '' : 'bg-red-100 text-red-800'}`}
                  style={mode === 'dark' ? { backgroundColor: 'rgba(127, 29, 29, 0.3)', color: '#fecaca' } : undefined}
                >
                  {translationWarning.message}
                </div>

                {/* Action button */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm rounded bg-[var(--color-accent)] text-white hover:opacity-80"
                    onClick={() => setTranslationWarning(null)}
                  >Got It</button>
                </div>
              </div>
            </div>,
            document.body
          )}

      {/* Custom blocking message for drag-drop attempts during spinner/streaming */}
      {showBlockingMessage && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          style={{ zIndex: 19500 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowBlockingMessage(false);
          }}
        >
          <div
            className="flex flex-col gap-3 p-4 rounded-md border border-[var(--color-accent)] bg-[var(--color-secondary)] text-[var(--color-text)] shadow-xl max-w-sm w-full mx-4"
            onMouseDown={(e) => e.stopPropagation()}
            role="alert"
            aria-live="assertive"
            aria-label="Action blocked message"
          >
            <div className="flex items-start gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="1" />
              </svg>
              <span className="text-sm font-medium">Action not allowed in current state. Please try uploading later.</span>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1 text-xs rounded bg-[var(--color-accent)] text-white hover:opacity-80"
                onClick={() => setShowBlockingMessage(false)}
              >OK</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Immediate local spinner - shows instantly without waiting for Redux */}
      {showImmediateSpinner && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{
            zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              border: '5px solid rgba(255,255,255,0.25)',
              borderTop: '5px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
        </div>,
        document.body
      )}

        {/* File Upload Dialog */}
        <FileUploadDialog
          isOpen={isFileUploadDialogOpen}
          onClose={handleFileUploadDialogClose}
          onUpload={handleFileUploadDialogUpload}
          onDropzoneClick={handleFileUploadDialogDropzoneClick}
          uploadedFiles={stagedFiles}
          isUploading={stagedFiles.some(f => f.status === 'uploading')}
          isDataAnalysisMode={selectedTool === 'data_analysis'}
          onRemoveFile={handleRemoveStagedFile}
        />

        {/* Microphone Selector Dialog - MOVED outside motion.form; see below */}
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      {confirmationConfig && (
        <ConfirmationDialog
          isOpen={showConfirmation}
          title={confirmationConfig.title}
          message={confirmationConfig.message}
          onConfirm={confirmationConfig.onConfirm}
          onCancel={() => {
            setShowConfirmation(false);
            setConfirmationConfig(null);
          }}
          confirmText={confirmationConfig.confirmText}
        />
      )}

      {/* Mobile Tools Menu Modal */}
      {isMobile && isMobileToolsMenuOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => setIsMobileToolsMenuOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          
          {/* Menu drawer from bottom */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="relative w-full bg-[var(--color-secondary)] rounded-t-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-accent)]">
              <h3 className="text-base font-semibold text-[var(--color-text)]">Select Tool</h3>
              <button
                type="button"
                onClick={() => setIsMobileToolsMenuOpen(false)}
                className="text-[var(--color-text)] opacity-70 hover:opacity-100"
                aria-label="Close menu"
              >
                <Cross2Icon width={20} height={20} />
              </button>
            </div>

            {/* Tool options list */}
            <div className="px-4 py-3 max-h-[60vh] overflow-y-auto">
              {chatOptions.map((toolOption) => {
                const IconComponent = toolOption.icon as React.ComponentType<any>;
                const isActive = selectedTool === toolOption.apiLabel;
                const isImageGenDisabled = isImageStreaming && !error && toolOption.apiLabel === 'image_generation';
                const isDisabled = isImageGenDisabled;

                return (
                  <button
                    key={toolOption.apiLabel}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      handleOptionClick(toolOption.apiLabel);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-all duration-200 ${
                      isActive 
                        ? 'bg-[#5E95ED] text-white border-2 border-[#5E95ED]' 
                        : 'bg-[var(--color-primary)] text-[var(--color-text)] border-2 border-transparent hover:border-[var(--color-accent)]'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="flex items-center justify-center w-6 h-6">
                      <IconComponent width={24} height={24} size={24} style={{ display: 'block' }} />
                    </span>
                    <span className="flex-1 text-left text-base font-medium pt-[0.25rem] mt-[1rem]">
                      {toolOption.label}
                      {isDisabled && toolOption.apiLabel === 'image_generation' && ' (Generating...)'}
                    </span>
                    {isActive && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
      {/* Voice Mode Error Dialog */}
      <ConfirmationDialog
        isOpen={showVoiceModeError}
        title="Error: Could not connect to Voice Mode"
        message={voiceModeErrorMessage}
        onConfirm={() => setShowVoiceModeError(false)}
        onCancel={() => setShowVoiceModeError(false)}
        confirmText="OK"
        showCancelButton={false}
      />

      {/* Microphone Permission Dialog - only shown when permission is denied */}
      <MicrophonePermissionDialog
        isOpen={showMicPermissionDialog}
        onClose={() => setShowMicPermissionDialog(false)}
      />
    </motion.form>
      )}

      {/* No Audio Detected Dialog - state lives in Redux so it survives navigation/remounts */}
      <ConfirmationDialog
        isOpen={showNoAudioDialog}
        title="No Audio Detected"
        message="We're not detecting any sound from your microphone. Would you like to switch to a different microphone?"
        onConfirm={() => {
          dispatch(setShowNoAudioDialog(false));
          setShowMicSelector(true);
        }}
        onCancel={() => dispatch(setShowNoAudioDialog(false))}
        confirmText="Switch Microphone"
        cancelText="Continue Anyway"
      />

      {/* Microphone Selector Dialog - outside motion.form so it renders even when voice mode hides the form */}
      <MicrophoneSelectorDialog
        isOpen={showMicSelector}
        onClose={() => setShowMicSelector(false)}
        onMicrophoneSelected={async (deviceId) => {
          logger.log('[ChatInput] Microphone selected:', deviceId);
          setSelectedMicDeviceId(deviceId);
          try {
            localStorage.setItem('selectedMicrophoneId', deviceId);
          } catch (e) {
            console.warn('[ChatInput] Failed to save microphone preference:', e);
          }
          setShowMicSelector(false);

          // Restart voice mode with new microphone if it was active
          if (isVoiceModeActive) {
            await voiceModeService.disconnect();
            dispatch(setVoiceModeActive(false));
            dispatch(setVoiceModeListening(false));
            setAudioLevel(0);
            hasCreatedAssistantMessageRef.current = false;
            hasActiveVoiceUserMessageRef.current = false;
            assistantResponsePendingRef.current = false;

            setTimeout(() => {
              startVoiceMode();
            }, 100);
          }
        }}
      />

      {/* Inactivity warning dialog - rendered at ChatInput level so it persists after voice mode ends */}
      <InactivityWarningDialog 
        isOpen={showInactivityWarning}
        countdown={inactivityCountdown}
        disconnectedByInactivity={disconnectedByInactivity}
        onDismiss={handleDismissInactivityWarning}
      />
    </>
  );
}